import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ActionType, DomPosition } from 'klinecharts'
import type { Chart, OverlayCreate, Point } from 'klinecharts'
import { Download, RotateCcw, Settings, Trash2, Upload } from 'lucide-react'
import { fetchSymbols, fetchSymbolMeta, type SymbolListItem } from '@/bot/adapters/symbolsFetcher'
import type { BotCode } from '@/bot/api'
import { WorkbenchChart, type WorkbenchChartStatus } from './WorkbenchChart'
import { WorkbenchTopToolbar } from './WorkbenchTopToolbar'
import { WorkbenchIdeaNav } from './WorkbenchIdeaNav'
import { WorkbenchDrawingRail } from './WorkbenchDrawingRail'
import { WorkbenchSymbolSearch } from './WorkbenchSymbolSearch'
import { WorkbenchIndicatorSearch } from './WorkbenchIndicatorSearch'
import { WorkbenchIndicatorSettings } from './WorkbenchIndicatorSettings'
import { WorkbenchSettingsDialog } from './WorkbenchSettingsDialog'
import { WorkbenchStatusLine } from './WorkbenchStatusLine'
import { WorkbenchPaneControls } from './WorkbenchPaneControls'
import { WorkbenchVolumeSettings } from './WorkbenchVolumeSettings'
import { WorkbenchFloatingToolbar } from './WorkbenchFloatingToolbar'
import { WorkbenchContextMenu } from './WorkbenchContextMenu'
import { WorkbenchEntryComposer } from './WorkbenchEntryComposer'
import { WorkbenchHoverNoteCard } from './WorkbenchHoverNoteCard'
import { WorkbenchNotes } from './WorkbenchNotes'
import { WorkbenchPinPrompt } from './WorkbenchPinPrompt'
import { nearestDrawing } from './pinHitTest'
import { CALLOUT_BG } from './noteStyle'
import ShowStateDialog from './ShowStateDialog'
import { ConfirmActionDialog } from '../ConfirmActionDialog/ConfirmActionDialog'
import { applyIndicator, defaultSettings, ensureIndicatorClone, mintEngineName, updateIndicatorStyles } from './indicators'
import { DEFAULT_FIB_LEVELS } from './customOverlays'
import { VOLUME_INDICATOR, volColors } from './volumeOverlay'
import { LEG_MARKERS, SETUP_MARKERS } from './legTypes'
import {
  type IdeaRole,
  ideaColor,
  ideaName,
  markLabel,
  PLACE_PROMPT,
  markLineLabel,
  TAP_PROMPT,
  roleOfSlot,
} from './tradeIdeaVocab'
import { WorkbenchIdeaNote, type IdeaNoteExit } from './WorkbenchIdeaNote'
import { WorkbenchDrawingNote } from './WorkbenchDrawingNote'
import { isNotableDrawing } from './drawingNoteVocab'
import {
  collectTradeIdeas,
  entryDragBounds,
  geometryRefusal,
  ideaDirection,
  isTradeMark,
  nextFreeSlot,
  pendingStep,
  toTradeMark,
  TRADE_MARK,
  type TradeIdea,
  type TradeIdeaMark,
} from './tradeIdeas'
import { WorkbenchTradeIdeaStrip } from './WorkbenchTradeIdeaStrip'
import { WorkbenchTradeIdeaPanel, IDEA_PANEL_W } from './WorkbenchTradeIdeaPanel'
import { WorkbenchIdeaPrompt } from './WorkbenchIdeaPrompt'
import { WorkbenchIdeaTouchGuide } from './WorkbenchIdeaTouchGuide'
import { dragReadout, levelReadout, MOVING_PROMPT, pipSizeFor } from './tradeIdeaReadout'
import { indicatorByName } from './catalog'
import { toAnchorTs } from './anchorTs'
import {
  composeWorkbenchState,
  foldCommitIntoSlice,
  MAX_SYMBOL_SLICES,
  parseWorkbenchState,
  serializeSymbolSlice,
  type AiPushState,
  type SavedDrawing,
  type SymbolSliceState,
  type WorkbenchSavedStateV2,
} from './workbenchState'
import {
  buildTransferFile,
  describeSlices,
  downloadTransferFile,
  mergeImportedSlices,
  readTransferFile,
  targetSymbolFor,
  transferFileName,
  type TransferRejection,
  type WorkbenchTransferFile,
} from './workbenchTransfer'
import { useWorkbenchAutosave } from './useWorkbenchAutosave'
import WorkbenchWalkthrough, { type WalkthroughMarkerClick } from './WorkbenchWalkthrough'
import { WorkbenchReplayStatus } from './WorkbenchReplayBar'
import useWalkthroughOverlay from './useWalkthroughOverlay'
import type { CanvasSeries } from './canvasSeries'
import BacktestOrderTooltip from '@/bot/components/BacktestOrderTooltip'
import { buildChartContext, drawingTitle, fmtFriendly, tradeLevelTitle } from './chartContext'
import { chartPush } from '@/bot/chartPush'
import { workbenchPersist } from '@/bot/workbenchPersist'
import { chartActivity } from '@/bot/chartActivity'
import { canvasSuspend } from '@/bot/canvasSuspend'
import { OnboardingTour, onboardingStorage } from './WorkbenchOnboarding'
import { ONB_STEPS } from './onboardingSteps'
import { useIsDark } from './useIsDark'
import { useIsPhone } from '@/hooks/useIsPhone'
import { useCoarsePointer } from '@/hooks/useCoarsePointer'
import { getIndicator, asOverlayId, asPixel, asPoint, paneDom, NOTE_BUBBLE_W } from './types'
import type { FibConfigPatch } from './WorkbenchFibLevels'
import type {
  ChartPin,
  NotePoint,
  ChartSettings,
  ChartTypeId,
  ComposerState,
  HoverNote,
  IndicatorInstance,
  IndicatorOption,
  IndicatorSettings,
  MenuSpec,
  SelectedDrawing,
  ToolDef,
  VolumeState,
  WorkbenchNote,
} from './types'

const DEFAULT_SYMBOL = 'EURUSD'
const DEFAULT_TIMEFRAME = 'H1'

/**
 * Tools whose next click PINS a note to whatever is under it — the Callout.
 * While one is armed, drawings are inert: a press on one picks it, never selects or drags it.
 */
const isPinTool = (tool: ToolDef | null | undefined): boolean => tool?.note === 'callout'

/** What a pin would take at a pixel, and the name it says it by. */
interface PinTarget {
  pin: ChartPin
  label: string
}

/** A live engine overlay, as returned by chart.getOverlayById (klinecharts exports no direct Overlay type here). */
type EngineOverlay = NonNullable<ReturnType<Chart['getOverlayById']>>

/** Default new-drawing colour (shared with the floating toolbar's memory). */
const DEFAULT_DRAW_COLOR = '#ffd60a'

// Tools that support press-drag-release. h/v lines (1 point) stay click-to-place.
// Channel is 3-point: the drag defines the base line, the parallel line gets an
// auto offset the user adjusts afterwards.
const DRAG_TOOLS = ['segment', 'rayLine', 'rectangle', 'fibRetracement', 'parallelStraightLine', 'arrow']

// How far a press may travel and still be a click/tap rather than a pan or drag.
// A finger wobbles 5–10px on a tap that feels perfectly still; a mouse doesn't.
const tapSlop = (touch: boolean) => (touch ? 10 : 4)
/**
 * Number of decimal places implied by a symbol's point size (e.g. 0.00001 → 5).
 * Drives the chart's price precision so the y-axis auto-scale and tick labels
 * match the instrument. Null = unknown → let the chart keep its default.
 */
function precisionFromPointSize(pointSize: number | null | undefined): number | null {
  if (!pointSize || pointSize <= 0) return null
  const decimals = Math.round(-Math.log10(pointSize))
  return Number.isFinite(decimals) && decimals >= 0 ? decimals : null
}

const dateLabel = (ts: number | undefined | null): string =>
  ts ? new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''

interface WorkbenchProps {
  bid: string
  /**
   * A one-shot request to run (or re-attach to) the walkthrough and show it — the parent's
   * `?wt=1`, set by the chat's "View walkthrough" and by the Auto-verify loop's finished run.
   * The Workbench takes it and calls `onWalkthroughChange(false)` to drop it.
   */
  walkthrough?: boolean
  onWalkthroughChange?: (on: boolean) => void
  /** Whether the bot has generated code at all — a walkthrough replays that code. */
  hasCode?: boolean
  /** Current code version identity. A change makes the stored walkthrough stale. */
  codeId?: number | null
  /**
   * The current code itself, for the walkthrough pane's Code tab — the same object the Backtest
   * tab shows. Display only: `codeId` above is its `codeId`, and stays the identity the overlay
   * hook compares, so the two cannot disagree.
   */
  currentCode?: BotCode | null
  /** Send the user to billing, for the out-of-usage error path. */
  onManageBilling?: () => void
}

// Module scope: the hooks compiler flags Date.now() in component scope as render
// impurity; this only ever runs inside chart/DOM event callbacks.
const nowMs = () => Date.now()

/** "1 note" / "3 notes" — plain enough for a confirmation that must be read at a glance. */
const countLabel = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`

/** The candle window currently on screen (ms), for the context header. Null
 * before any candles have landed. */
function visibleWindow(chart: Chart): { fromTs: number; toTs: number } | null {
  const list = chart.getDataList()
  if (list.length === 0) return null
  const vr = chart.getVisibleRange()
  const clamp = (i: number) => Math.min(Math.max(i, 0), list.length - 1)
  return { fromTs: list[clamp(vr.from)].timestamp, toTs: list[clamp(vr.to - 1)].timestamp }
}

/** Legacy persisted sentItems carried the candle style in the market item
 * ("EURUSD · 1h · candles"); appearance no longer participates in diffs. */
function normalizeSentItems(si: Record<string, string> | null): Record<string, string> | null {
  if (!si || typeof si.market !== 'string') return si
  const parts = si.market.split(' · ')
  return parts.length > 2 ? { ...si, market: parts.slice(0, 2).join(' · ') } : si
}

/**
 * The "Create" tab: a TradingView-style charting workbench for sketching a
 * strategy idea visually — candles + indicators + drawings + Buy/Sell trade
 * markers with captured reasoning + on-chart notes. The whole chart state is
 * auto-persisted per bot (`bid`) as a per-symbol map of slices — each symbol
 * keeps its own drawings/notes/view/indicators/AI bookkeeping, restored when
 * the user switches back to it — last-write-wins across tabs; "Show State"
 * reveals the exact persisted JSON.
 */
export function Workbench({
  bid,
  walkthrough,
  onWalkthroughChange,
  hasCode = false,
  codeId = null,
  currentCode = null,
  onManageBilling,
}: WorkbenchProps) {
  const isDark = useIsDark()
  // On a narrow screen the instruction and the readout sit at the top of the chart instead of
  // beside the pointer, for placing and for moving a level (PROD-3131). By width, as the
  // prototype does, so a narrow desktop window shows it too; only the verb follows the pointer
  // ("Tap" for a finger, "Click" for a mouse).
  const isPhone = useIsPhone()
  const coarsePointer = useCoarsePointer()
  const topReadout = isPhone

  // ---- Market spine (kept from the existing app implementation) -------------
  const [symbols, setSymbols] = useState<SymbolListItem[]>([])
  const [symbol, setSymbol] = useState('')
  const [timeframe, setTimeframe] = useState(DEFAULT_TIMEFRAME)
  const [anchorTs, setAnchorTs] = useState<number | null>(null)
  const [chartType, setChartType] = useState<ChartTypeId>('candle')
  const [status, setStatus] = useState<WorkbenchChartStatus>({ loading: false, error: null, empty: false })
  // The walkthrough is shown on the chart (persisted). Only ever has an effect on the run's own
  // symbol — the overlay derives that from the match.
  const [showWalkthrough, setShowWalkthrough] = useState(false)

  const [chart, setChart] = useState<Chart | null>(null)
  // The candle series behind the canvas chart — what the walkthrough's bar replay cuts.
  const [series, setSeries] = useState<CanvasSeries | null>(null)
  // The file picker behind "Import chart setup…" — a menu item cannot be an
  // <input>, so it clicks this one.
  const importInputRef = useRef<HTMLInputElement | null>(null)
  const chartRef = useRef<Chart | null>(null) // event-callback mirror of `chart`
  const onReady = useCallback((c: Chart | null, s: CanvasSeries | null = null) => {
    chartRef.current = c
    setChart(c)
    setSeries(s)
  }, [])

  // ---- Persistence (per-bot auto-save + mount restore, per-symbol slices) -----
  // False until the saved state (if any) has been fully applied; gates autosave.
  const [restoreDone, setRestoreDone] = useState(false)
  // Every known symbol's saved chart environment. The ACTIVE symbol's entry
  // may be stale — the compose step overrides it with a live snapshot.
  const bySymbolRef = useRef<Record<string, SymbolSliceState>>({})
  // Slice waiting to be applied once its symbol's candles land (mount or
  // symbol switch). null = nothing pending (unseen symbol inherits the live
  // environment). Also guards autosave from serializing the blank mid-switch
  // canvas as the incoming symbol's state.
  const pendingRestoreRef = useRef<{ symbol: string; slice: SymbolSliceState; isMount: boolean } | null>(null)
  // When the active symbol became active — a STABLE touchedAt for the live
  // slice (a fresh Date.now() per serialize would defeat the autosave dedupe).
  const activeSinceRef = useRef(0)

  // ---- AI chart context (staged for the chat, sent incrementally) -------------
  // What the AI has seen of the ACTIVE symbol: diff base for incremental
  // sends, persisted per slice via aiPush and swapped on symbol switch.
  const aiPushRef = useRef<AiPushState>({ sentAt: null, sentItems: null, sentViews: null })
  // Last commit sentAt already folded into the per-symbol bookkeeping —
  // dedupes store events (aiPushRef.sentAt can't: it's per-symbol now).
  const lastSeenCommitRef = useRef(0)
  /** The mount restore applied a commit the saved blob did not hold yet — write it once armed. */
  const reconciledRef = useRef(false)
  // Gates context rebuilds until the restore's setState storm has settled.
  const aiCountReadyRef = useRef(false)
  const ctxSeqRef = useRef(0) // per-drawing context ids
  // Mirrors drawIdsRef.current.size into render-safe state.
  const [drawCount, setDrawCount] = useState(0)

  // ---- Onboarding (first-run informative tour) ---------------------------------
  // number = 0-based index into ONB_STEPS; advanced only by the tour's Next button.
  const [onbStep, setOnbStep] = useState<'pending' | 'done' | number>('pending')

  // ---- Chrome / dialogs ------------------------------------------------------
  const [chartSettings, setChartSettings] = useState<ChartSettings>({})
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [symbolSearchOpen, setSymbolSearchOpen] = useState(false)
  const [symbolSeed, setSymbolSeed] = useState('')
  const [indicatorsOpen, setIndicatorsOpen] = useState(false)
  const [showState, setShowState] = useState(false)
  const [stateSnapshot, setStateSnapshot] = useState<unknown>(null)
  // Chart-wrapper size, for clamping the HTML float layers (composer / hover card).
  const [wrapSize, setWrapSize] = useState({ w: 0, h: 0 })

  // ---- Tools / selection -----------------------------------------------------
  const [activeTool, setActiveTool] = useState('cursor')
  const [magnet, setMagnet] = useState(false)
  const [cmdHeld, setCmdHeld] = useState(false)
  const [editing, setEditing] = useState(false) // a drawing's handle is being dragged
  const [selected, setSelected] = useState<SelectedDrawing | null>(null)
  /**
   * The selection whose comment box the trader closed with its ×.
   *
   * A selected drawing puts up TWO windows — the editing toolbar above it and
   * the comment box below — so the × has to be able to dismiss one without
   * dismissing the other, or it would be a second Deselect. Keyed by
   * `selected.key`, so it lapses the moment another drawing is picked: closing
   * one line's box says nothing about the next line's.
   */
  const [noteDismissedKey, setNoteDismissedKey] = useState<string | null>(null)
  // Menus capture the drawing count when they open (drawings live in a ref, not state).
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; drawCount: number } | null>(null)
  const [deleteMenu, setDeleteMenu] = useState<{ x: number; y: number; drawCount: number } | null>(null)

  // ---- Indicators / volume ---------------------------------------------------
  const [indicators, setIndicators] = useState<IndicatorInstance[]>([])
  const instCounterRef = useRef(0)
  const [settingsId, setSettingsId] = useState<string | null>(null)
  const [volume, setVolume] = useState<VolumeState>({ present: true, visible: true, up: null, down: null })
  const [volumeSettingsOpen, setVolumeSettingsOpen] = useState(false)

  // ---- Trade ideas / notes ---------------------------------------------------
  // The idea open for editing. Carries the whole descriptor rather than just an
  // id, because an idea that has just been started has no marks yet and so
  // cannot be derived from the chart. The derived idea takes over the moment
  // the first mark lands; the two can't disagree, since every mark carries the
  // same seq/colour/direction.
  const [ideaEdit, setIdeaEdit] = useState<{
    ideaId: string
    seq: number
    color: string
    dir: 'buy' | 'sell'
  } | null>(null)
  const ideaEditRef = useRef<typeof ideaEdit>(null)
  /** The step the next chart click fills. Null = not placing. */
  const [armedStep, setArmedStep] = useState<IdeaRole | null>(null)
  const armedStepRef = useRef<IdeaRole | null>(null)
  const [ideaPanelOpen, setIdeaPanelOpen] = useState(false)
  const [ideaExpanded, setIdeaExpanded] = useState<ReadonlySet<string>>(() => new Set())
  const [ideaGuide, setIdeaGuide] = useState<{ x: number; y: number; refusal: string | null; price?: number } | null>(null)
  // The readout riding a dragged stop loss or take profit (PROD-3131).
  /**
   * A pointer is on the chart, so there is a price under it (PROD-3131): a mouse while it is over
   * the chart, a finger while it is down.
   */
  const [pointerOnChart, setPointerOnChart] = useState(false)
  const [dragGuide, setDragGuide] = useState<{
    x: number
    y: number
    role: IdeaRole
    readout: string | null
    /** The level is on the wrong side of its entry: dropped there, it goes back. */
    refusal: string | null
    color: string
    ideaId: string
    /** Width of the candles' plot, without the price axis, for the bar at the top. */
    plotW: number
  } | null>(null)
  /** Derived from the marks on the chart, refreshed by notifyEdit — drawings
   *  live in a ref, so this is pushed rather than computed during render. */
  const [ideas, setIdeas] = useState<TradeIdea[]>([])
  const ideaSeqRef = useRef(0)
  /** The open note box for a trade-idea level. Not the legacy composer. */
  const [ideaNote, setIdeaNote] = useState<{
    markId: string
    role: IdeaRole
    x: number
    y: number
    initial: string
  } | null>(null)
  const ideaNoteRef = useRef<typeof ideaNote>(null)
  ideaNoteRef.current = ideaNote
  /**
   * THE confirmation question — one surface, not one dialog per destructive act.
   *
   * The typed gate, the Escape guard and "only one question at a time" are then
   * written once, so the next irreversible act cannot arrive carrying its own
   * hand-rolled version of them. This is the prototype's shared confirm trigger,
   * scoped to the component that needs it.
   *
   * The wording is baked at the moment of ASKING, so the question still names
   * what it destroys after the confirm has emptied the chart of it.
   *
   * Closing flips `open` and KEEPS the payload, because the dialog animates out
   * and would otherwise spend that half second rendering its own defaults —
   * "Are you sure? / Confirm" — over a chart that has already been wiped.
   */
  const [confirmAsk, setConfirmAsk] = useState<{
    open: boolean
    title: string
    body: ReactNode
    confirmLabel: string
    /**
     * The word that unlocks it. A wipe always asks for it; an import only when
     * it would overwrite a setup that actually holds work, so importing onto a
     * blank strategy — the reason the feature exists — stays one click.
     */
    typeToConfirm?: string
    /** Red confirm button. On by default: most questions here destroy work. */
    destructive?: boolean
    /** A report with nothing to decline (a failed import) drops Cancel. */
    hideCancel?: boolean
    action: () => void
  } | null>(null)
  const closeConfirmAsk = () => setConfirmAsk((a) => (a ? { ...a, open: false } : null))
  /**
   * The step to arm once the open note box closes.
   *
   * Arming is deferred deliberately: the box IS the way forward, so the chart
   * only starts listening again once it is out of the way. Arm on placement
   * instead and the very click that dismisses the box also plants the next
   * level. Only a fresh placement sets a baton, so a box re-opened on an
   * existing level re-arms nothing when it closes.
   */
  const pendingNextRef = useRef<IdeaRole | null>(null)
  /**
   * A click-away is reported on pointerdown, so its `click` is still travelling
   * and would land on the chart. Eat exactly one.
   */
  const swallowChartClickRef = useRef(false)
  /** Pre-drag price of a trade level, to put it back if the drag ends illegal. */
  const preDragRef = useRef<{ id: string; value: number } | null>(null)
  const [composer, setComposer] = useState<ComposerState | null>(null)
  const [hoverNote, setHoverNote] = useState<HoverNote | null>(null)
  const [notes, setNotes] = useState<WorkbenchNote[]>([])
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const noteIdRef = useRef(0)
  /** The guide under the pointer while a pin tool is armed: what a click would pin to. */
  const [pinGuide, setPinGuide] = useState<{ x: number; y: number; target: string } | null>(null)
  /** The drawing the engine handed an armed pin tool's click to — its onClick runs first. */
  const pinPickRef = useRef<string | null>(null)
  // Bumped on scroll/zoom so pixel-derived layers (notes, pane legends) re-render.
  const [vpNonce, setVpNonce] = useState(0)

  // ---- Refs (live mirrors for long-lived overlay callbacks) ------------------
  const chartWrapRef = useRef<HTMLDivElement | null>(null)
  const defaultBarSpaceRef = useRef<number | null>(null)
  const activeToolRef = useRef<ToolDef | null>(null)
  const progressIdRef = useRef<string | null>(null) // the engine's in-progress click-draw overlay
  const previewIdRef = useRef<string | null>(null) // live preview overlay during a drag
  const pressedIdRef = useRef<string | null>(null) // overlay whose handle is being edited
  const pressedIsHandleRef = useRef(false)
  const drawRef = useRef<{ x: number; y: number; pt: Partial<Point> | null; dragging: boolean; touch: boolean }>({
    x: 0,
    y: 0,
    pt: null,
    dragging: false,
    touch: false,
  })
  const pressPtRef = useRef<{ x: number; y: number; t: number; touch: boolean } | null>(null) // last chart-area pointerdown (click-vs-pan/tap discrimination)
  const activePointersRef = useRef(0) // concurrent pointers on the chart area
  const sawMultiTouchRef = useRef(false) // gesture had ≥2 pointers (pinch) → never a tap
  const lastTouchTapRef = useRef(0) // when a touch tap ran the click routing (dedupe vs compat click)
  const lastPressTouchRef = useRef(false) // the latest chart-area press was a finger
  const deferredUnlockRef = useRef<(() => void) | null>(null) // pan/zoom unlock waiting for the next press
  const magnetRef = useRef(false)
  const cmdHeldRef = useRef(false)
  const selectedRef = useRef<SelectedDrawing | null>(null)
  const selDotRef = useRef<string | null>(null) // the click-selection marker dot
  const drawingClickRef = useRef(false) // a drawing was just clicked → skip placing a dot
  const lastManipEndRef = useRef(0)
  const drawIdsRef = useRef<Set<string>>(new Set()) // tracked user drawings (engine has no "get all")
  // Last-used style per drawing type, seeded into the next new instance (in-memory only).
  const drawStyleRef = useRef<Record<string, { line?: Record<string, unknown>; polygon?: Record<string, unknown>; extendData?: Record<string, unknown> }>>({})

  useEffect(() => {
    magnetRef.current = magnet
  }, [magnet])
  useEffect(() => {
    selectedRef.current = selected
  }, [selected])
  useEffect(() => {
    ideaEditRef.current = ideaEdit
  }, [ideaEdit])
  useEffect(() => {
    armedStepRef.current = armedStep
  }, [armedStep])
  /** Read by the key handler, which is bound once and keyed on `chart`. */
  const confirmAskRef = useRef(false)
  useEffect(() => {
    confirmAskRef.current = !!confirmAsk?.open
  }, [confirmAsk])
  const symbolRef = useRef('')
  useEffect(() => {
    symbolRef.current = symbol
  }, [symbol])

  // ---- Walkthrough trade card ------------------------------------------------------
  // Order-details card opened by clicking one of the walkthrough's trade arrows (the Backtest
  // tab's click tooltip, with the run's ticket numbers). Follows its arrow on pan/zoom because
  // the pixel anchor is recomputed every render (vpNonce bumps).
  const [wtOrderCard, setWtOrderCard] = useState<WalkthroughMarkerClick | null>(null)
  const orderMarkerClickRef = useRef(false) // marker click → the same click must not dismiss the card
  /** Bumped on every drawing edit: the walkthrough's replay reveal reads it. */
  const [drawVersion, setDrawVersion] = useState(0)

  const handleWtMarkerClick = useCallback((click: WalkthroughMarkerClick) => {
    orderMarkerClickRef.current = true
    drawingClickRef.current = true // suppress the candle click-selection dot
    setWtOrderCard(click)
  }, [])

  // ---- Walkthrough replay cutoff ------------------------------------------------------
  // True while the replay cuts the canvas at its playhead. Two things must not run against a
  // truncated chart: the AI context rebuild, and importing a chart setup.
  const cutoffActiveRef = useRef(false)

  // ---- Autosave ---------------------------------------------------------------
  // Debounced persist of the full chart state; `markDirty` is a no-op until the
  // mount restore has applied, which also inertizes the restore's setState storm.
  // Never called during render — safe to read the push store here.
  const currentAiPush = (): AiPushState => ({
    sentAt: aiPushRef.current.sentAt,
    sentItems: aiPushRef.current.sentItems,
    ...(aiPushRef.current.sentViews ? { sentViews: aiPushRef.current.sentViews } : {}),
  })

  // The ACTIVE symbol's chart environment, serialized live off the engine.
  const snapshotActiveSlice = (): SymbolSliceState | null =>
    chart
      ? serializeSymbolSlice({
          chart,
          timeframe,
          chartType,
          indicators,
          volume,
          notes,
          noteCounter: noteIdRef.current,
          drawIds: drawIdsRef.current,
          aiPush: currentAiPush(),
          touchedAt: activeSinceRef.current,
        })
      : null

  // The full persisted blob: every known slice, with the active symbol's
  // entry overridden by a live snapshot — EXCEPT mid-switch, when the canvas
  // is blank and the pending (saved) slice is the truth; serializing the
  // blank canvas would clobber the incoming symbol's state.
  const composeCurrentState = (): WorkbenchSavedStateV2 | null => {
    if (!symbol) return null
    const pending = pendingRestoreRef.current
    const activeSlice = pending && pending.symbol === symbol ? pending.slice : snapshotActiveSlice()
    if (!activeSlice) return null
    return composeWorkbenchState({
      activeSymbol: symbol,
      chartSettings,
      ...(showWalkthrough ? { showWalkthrough: true } : {}),
      // Unsent changes ride along, so a reload keeps offering them.
      staged: chartPush.get(bid).staged,
      bySymbol: { ...bySymbolRef.current, [symbol]: activeSlice },
    })
  }

  const { markDirty, invalidate: invalidateSaved } = useWorkbenchAutosave({
    bid,
    enabled: restoreDone,
    getState: composeCurrentState,
  })

  const selectedSymbol = symbols.find((s) => s.symbol === symbol)
  const pricePrecision = precisionFromPointSize(selectedSymbol?.point_size)
  const pipSize = pipSizeFor(selectedSymbol?.point_size)
  // Read by the overlay drag handlers, which are bound when an overlay is created.
  const pipSizeRef = useRef(pipSize)
  useEffect(() => {
    pipSizeRef.current = pipSize
  }, [pipSize])
  const prec = () => chart?.getPriceVolumePrecision().price ?? 2

  // ---- Chart context: staged for the composer's Send to AI --------------------
  // Whenever something the user drew differs from what the AI last saw, the
  // composer offers "Chart updated · Send to AI". Rather than snapshotting
  // on the click, we rebuild the context whenever something the AI would care
  // about changed and leave it staged. Debounced, so a note drag (hundreds of
  // events) is one rebuild, and diffed against what was last sent, so a send
  // only ever carries the delta — the server replays every stored update on
  // every turn, so full snapshots per send would eat the token budget.
  const refreshChartContext = () => {
    const c = chartRef.current
    if (!c || !aiCountReadyRef.current) return
    // A walkthrough replay has the chart cut at its playhead; the context describes the chart.
    if (cutoffActiveRef.current) return
    // Mid-switch the canvas is blank — never diff it; the restore refreshes.
    if (pendingRestoreRef.current) return
    const state = serializeSymbolSlice({
      chart: c,
      timeframe,
      chartType,
      indicators,
      volume,
      notes,
      noteCounter: noteIdRef.current,
      drawIds: drawIdsRef.current,
      touchedAt: activeSinceRef.current,
    })
    const ctx = buildChartContext(
      { symbol, ...state },
      {
        precision: pricePrecision ?? prec(),
        window: visibleWindow(c),
        prevSentItems: aiPushRef.current.sentItems,
        prevSentViews: aiPushRef.current.sentViews ?? null,
      },
    )
    // Nothing the user DREW differs from the last send (edits that netted out,
    // pan/zoom, or only an indicator/market change — those ride along with the
    // next drawn change rather than raising the composer on their own).
    const { added, edited, removed } = ctx.counts
    if (added + edited + removed === 0) {
      chartPush.clearStaged(bid)
      return
    }
    chartPush.stage(bid, {
      label: ctx.label,
      text: ctx.text,
      items: ctx.items,
      views: ctx.views,
      first: ctx.first,
      changes: ctx.changes,
      counts: ctx.counts,
      omitted: ctx.omitted,
      symbol,
      atMs: nowMs(),
    })
  }
  // Ref pattern (as in useWorkbenchAutosave): the debounce timer must read the
  // latest state, not the closure it was scheduled from.
  const refreshContextRef = useRef(refreshChartContext)
  useLayoutEffect(() => {
    refreshContextRef.current = refreshChartContext
  })
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleContextRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null
      refreshContextRef.current()
    }, 300)
  }, [])
  useEffect(
    () => () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current)
    },
    [],
  )

  /** An edit landed: rebuild once edits settle. */
  const touchContext = scheduleContextRefresh

  /**
   * The drawing count, into render state AND out to the chat.
   *
   * The chat has to answer the same question `wtRunBlockedReason` answers with `drawCount === 0`
   * — after code generation it offers a walkthrough, which replays the period drawn on — from a
   * column that stays mounted when this subtree does not (the Backtest and Optimise tabs unmount
   * the workbench). Hence the store; see bot/chartActivity.ts.
   *
   * Called ONLY where the number is the truth: a settled edit, and a restore that has landed.
   * Notably NOT from the symbol-switch wipe, whose 0 is a blank canvas waiting for the incoming
   * slice — publishing there would flip the chat's offer and back within a second, and would
   * overwrite the cached count the offer falls back to on a tab that never mounts this subtree.
   */
  const publishDrawCount = (n: number) => {
    setDrawCount(n)
    chartActivity.setDrawCount(bid, n)
  }

  // Engine-held drawing mutations: persist AND rebuild the chart context.
  const notifyEdit = () => {
    publishDrawCount(drawIdsRef.current.size)
    setDrawVersion((v) => v + 1)
    setIdeas(readIdeas()) // the single funnel for engine-held changes
    markDirty()
    touchContext()
  }

  // React-state mutations (indicators/volume/notes/settings/market/viewport) all
  // funnel through here; engine-held drawing mutations call markDirty explicitly.
  useEffect(() => {
    markDirty()
  }, [symbol, timeframe, chartType, chartSettings, indicators, volume, notes, vpNonce, showWalkthrough, markDirty])

  // Context-rebuild trigger: fire only when something the AI context reflects
  // may have changed — market (symbol/timeframe), indicator setups, volume
  // presence and notes. Candle style, appearance settings and the viewport
  // never reach the context, so they must not trigger a rebuild. The semantic
  // fingerprint both shields against state identity churn (e.g. bandId
  // stamping after a restore) and avoids timer churn on unrelated renders.
  const aiFpRef = useRef<string | null>(null)
  useEffect(() => {
    const fp = JSON.stringify([
      symbol,
      timeframe,
      indicators.map((i) => [i.name, i.settings.params]),
      volume.present,
      // A callout's pin is part of what it says ("pinned to <label> at …"): re-pinning its tail
      // changes the context without moving the note.
      notes
        .filter((n) => n.text.trim())
        .map((n) => [n.id, n.text, n.pos.timestamp, n.pos.value, n.kind === 'callout' ? (n.pin ?? null) : null, n.pinLabel ?? null]),
    ])
    if (fp === aiFpRef.current) return
    aiFpRef.current = fp
    touchContext()
  }, [symbol, timeframe, indicators, volume, notes, bid, touchContext])

  // Arm the rebuild only after the restore commit has fully flushed — this
  // effect is declared after the trigger effect, so within the restore's commit
  // the trigger still sees the gate closed. The refresh then rebuilds the
  // context from the restored chart.
  useEffect(() => {
    if (restoreDone) {
      aiCountReadyRef.current = true
      scheduleContextRefresh()
      // Autosave seeded its "already saved" marker from the restored state just now; a commit the
      // restore folded in is not in the saved blob yet, so it has to be written regardless.
      if (reconciledRef.current) {
        reconciledRef.current = false
        invalidateSaved()
        markDirty()
      }
    }
  }, [restoreDone, scheduleContextRefresh, invalidateSaved, markDirty])

  // A Send to AI that commits while no Workbench is mounted is persisted by BotDetail instead
  // (bot/workbenchPersist.ts); while this one is, it folds and saves its own commits.
  useEffect(() => chartPush.attachWorkbench(bid), [bid])

  // Observe chat-side commits (the send itself happens in BotDetail): fold the
  // sent items into the aiPush bookkeeping OF THE SYMBOL the context was built
  // from — the user may have switched symbols between staging and sending — so
  // the next rebuild diffs against what the AI has actually seen.
  useEffect(() => {
    let prev = chartPush.get(bid)
    return chartPush.subscribe((s) => {
      if (s.bid !== bid) return
      const prevStaged = prev.staged
      prev = s
      const lc = s.lastCommit
      if (lc && lc.sentAt > lastSeenCommitRef.current) {
        lastSeenCommitRef.current = lc.sentAt
        const sent: AiPushState = { sentAt: lc.sentAt, sentItems: lc.sentItems, sentViews: lc.sentViews }
        if (lc.symbol === symbolRef.current) {
          aiPushRef.current = sent
        } else {
          // In place: a pending symbol switch holds this same slice object.
          const sl = bySymbolRef.current[lc.symbol]
          if (sl) Object.assign(sl, foldCommitIntoSlice(sl, lc))
        }
        markDirty() // persist the new diff base
        // An edit made while the send was in flight left a newer snapshot staged,
        // diffed against the OLD base — rebuild it against the new one.
        if (s.staged) scheduleContextRefresh()
      }
      // Staged / cleared / sent — persist it, since it rides in the saved blob.
      if (prevStaged !== s.staged) markDirty()
    })
  }, [bid, markDirty, scheduleContextRefresh])

  // ---- Symbols / anchor (kept from the existing implementation) --------------
  // Mount: fetch the symbol list and any persisted workbench state together.
  // Slices for delisted symbols are dropped individually — drawings/notes/view
  // are symbol-bound, so a partial restore of one would lie.
  useEffect(() => {
    let active = true
    Promise.all([
      fetchSymbols(),
      // Queued behind any save of this bot still in flight — an unmount flush, or a send
      // persisted while this Workbench was unmounted — so it reads what they wrote.
      workbenchPersist
        .get(bid)
        .then((d) => parseWorkbenchState(d?.state))
        .catch(() => null), // persistence must never block the workbench
    ])
      .then(([list, saved]) => {
        if (!active) return
        setSymbols(list)
        const listed = new Set(list.map((s) => s.symbol))
        const slices: Record<string, SymbolSliceState> = {}
        if (saved) {
          for (const [sym, slice] of Object.entries(saved.bySymbol)) {
            if (listed.has(sym)) slices[sym] = slice
          }
        }
        // Active symbol: the saved pointer if it survived, else the most
        // recently touched surviving slice, else the plain default.
        const activeSym =
          saved && listed.has(saved.activeSymbol) && slices[saved.activeSymbol]
            ? saved.activeSymbol
            : (Object.entries(slices).sort((a, b) => b[1].touchedAt - a[1].touchedAt)[0]?.[0] ?? null)

        activeSinceRef.current = nowMs()

        if (saved && activeSym) {
          const slice = slices[activeSym]
          bySymbolRef.current = slices
          pendingRestoreRef.current = { symbol: activeSym, slice, isMount: true }
          setSymbol(activeSym)
          setTimeframe(slice.timeframe)
          setChartType(slice.chartType)
          setChartSettings(saved.chartSettings)
          setVolume(slice.volume)
          setNotes(slice.notes)
          setShowWalkthrough(saved.showWalkthrough ?? false)
          noteIdRef.current = slice.noteCounter
          // Indicators / drawings / view apply once the first candle load lands
          // (handleChartStatus); autosave stays disabled until then.
        } else {
          const preferred = list.find((s) => s.symbol === DEFAULT_SYMBOL) ?? list[0]
          if (preferred) setSymbol(preferred.symbol)
          setRestoreDone(true)
        }

        // Seed the AI-push bookkeeping. A commit that happened while this
        // workbench was unmounted (send from another tab of the app) wins
        // over the persisted snapshot — for the symbol it was built from.
        const st = chartPush.get(bid)
        const lc = st.lastCommit
        if (lc) {
          lastSeenCommitRef.current = lc.sentAt
          if (lc.symbol !== activeSym) {
            const sl = bySymbolRef.current[lc.symbol]
            const folded = sl ? foldCommitIntoSlice(sl, lc) : sl
            if (sl && folded && folded !== sl) {
              bySymbolRef.current[lc.symbol] = folded
              reconciledRef.current = true
            }
          }
        }
        const savedAi = activeSym ? (slices[activeSym]?.aiPush ?? null) : null
        if (lc && lc.symbol === activeSym && lc.sentAt > (savedAi?.sentAt ?? 0)) {
          aiPushRef.current = { sentAt: lc.sentAt, sentItems: lc.sentItems, sentViews: lc.sentViews }
          reconciledRef.current = true
        } else {
          aiPushRef.current = {
            sentAt: savedAi?.sentAt ?? null,
            sentItems: normalizeSentItems(savedAi?.sentItems ?? null),
            sentViews: savedAi?.sentViews ?? null,
          }
          // Persisted unsent changes are re-staged so a reload keeps offering
          // them; dropped if their symbol was delisted meanwhile. The
          // post-restore refresh rebuilds them from the live chart either way.
          const savedStaged = saved?.staged && listed.has(saved.staged.symbol) ? saved.staged : null
          chartPush.seedIfFresh(bid, {
            hasPushes: (savedAi?.sentItems ?? null) != null,
            staged: savedStaged,
          })
        }

        // Onboarding: first run only (?onboard=reset / ?onboard=1 for dev), and
        // never over a chart (any symbol's) that already has trade markers.
        const onbOverride = new URLSearchParams(window.location.search).get('onboard')
        if (onbOverride === 'reset') onboardingStorage.reset()
        const hasTradeMarkers = Object.values(slices).some((sl) =>
          sl.drawings.some((d) => ['buyEntry', 'sellEntry', 'exitPoint', TRADE_MARK].includes(d.name)),
        )
        const eligible = onbOverride === '1' || (!onboardingStorage.isDone() && !hasTradeMarkers)
        setOnbStep(eligible ? 0 : 'done')

        // Same question, second consumer: a strategy already carrying trade
        // markers has had its first idea, so the composer nudge is spent. Sealed
        // WITHOUT raising it — this is a seed from persistence, not a save.
        if (hasTradeMarkers) chartActivity.seedIdeaSaved(bid)
      })
      .catch((err: Error) => {
        if (active) {
          setStatus({ loading: false, error: err.message, empty: true })
          setRestoreDone(true)
        }
      })
    return () => {
      active = false
    }
  }, [bid])

  useEffect(() => {
    if (!symbol) return
    let active = true
    fetchSymbolMeta(symbol)
      .then((meta) => {
        if (!active) return
        setAnchorTs(meta?.date_end ? toAnchorTs(meta.date_end) : null)
      })
      .catch(() => {
        if (active) setAnchorTs(null)
      })
    return () => {
      active = false
    }
  }, [symbol])

  // ---- Viewport nonce: keep pixel-anchored HTML layers glued to the chart ----
  useEffect(() => {
    if (!chart) return
    const bump = () => {
      setVpNonce((n) => n + 1)
      // The floating toolbar is pixel-anchored to the selection click; the
      // chart just moved under it — drop the selection. The engine keeps its
      // own click state, so re-clicking the same drawing reopens the toolbar
      // via the overlay onClick handler (onSelected only fires on a CHANGE of
      // clicked overlay). setSelected(null) when already null is a React
      // same-value bail — plain pans stay cheap.
      setSelected(null)
    }
    chart.subscribeAction(ActionType.OnScroll, bump)
    chart.subscribeAction(ActionType.OnZoom, bump)
    return () => {
      chart.unsubscribeAction(ActionType.OnScroll, bump)
      chart.unsubscribeAction(ActionType.OnZoom, bump)
    }
  }, [chart])

  // Remember the default zoom (bar space) so "Reset chart view" can restore it.
  // Capture once — the view restore may setBarSpace to a saved zoom later.
  useEffect(() => {
    if (chart && defaultBarSpaceRef.current == null) defaultBarSpaceRef.current = chart.getBarSpace()
  }, [chart])

  // A pan/zoom unlock still waiting for the next press (see endDrawing) dies with the chart.
  useEffect(
    () => () => {
      if (deferredUnlockRef.current) window.removeEventListener('pointerdown', deferredUnlockRef.current, true)
      deferredUnlockRef.current = null
    },
    [chart],
  )

  // Track the wrapper's size (ResizeObserver fires once on observe, then on resizes).
  useEffect(() => {
    const el = chartWrapRef.current
    if (!el) return
    const ro = new ResizeObserver(() => setWrapSize({ w: el.clientWidth, h: el.clientHeight }))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Shift + scroll pans the chart horizontally (TradingView convention): lets the
  // user move the chart while an entry-setup tool is armed (drag-pan is locked
  // during placement). Native listener with passive:false to stop page scroll.
  useEffect(() => {
    const el = chartWrapRef.current
    if (!el || !chart) return
    const onWheel = (e: WheelEvent) => {
      if (e.shiftKey) {
        e.preventDefault()
        chart.scrollByDistance(e.deltaX || e.deltaY, 0)
        return
      }
      // Horizontal two-finger swipes over the chart must never turn into the
      // browser's back/forward navigation gesture — cancel the default even
      // when the event lands on an HTML layer (legends, notes, pills) or while
      // a tool has chart scrolling locked. klinecharts still receives the
      // event and pans; only the browser's default action is suppressed.
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) e.preventDefault()
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [chart])

  // Cursor mode → pointer (double-click opens settings) on the candle pane and
  // every sub-pane; a drawing tool → crosshair.
  useEffect(() => {
    if (!chart) return
    const cur = activeTool === 'cursor' ? 'pointer' : 'crosshair'
    const panes = ['candle_pane', ...indicators.filter((i) => i.paneId !== 'candle_pane').map((i) => i.paneId)]
    ;[...new Set(panes)].forEach((pid) => {
      const d = paneDom(chart, pid)
      if (d) d.style.cursor = cur
    })
  }, [chart, activeTool, indicators])

  // Type-to-search (TV behaviour): typing a letter/number on the chart opens the
  // symbol modal seeded with that char. Ignored while typing in a field, while a
  // modal/composer/note edit is open, or when a modifier is held.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (symbolSearchOpen || settingsOpen || indicatorsOpen || settingsId || volumeSettingsOpen) return
      if (composer || editingNoteId) return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const t = e.target as HTMLElement | null
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(t?.tagName ?? '') || t?.isContentEditable
      if (typing) return
      if (e.key.length === 1 && /[a-zA-Z0-9]/.test(e.key)) {
        setSymbolSeed(e.key)
        setSymbolSearchOpen(true)
        e.preventDefault()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [symbolSearchOpen, settingsOpen, indicatorsOpen, settingsId, volumeSettingsOpen, composer, editingNoteId])

  // ---- Drawing bookkeeping ----------------------------------------------------
  // Stable per-drawing id for the AI chart-context diff (overlay engine ids are
  // NOT stable across restores; ctxId rides extendData, which round-trips).
  const stampCtxId = (id: string) => {
    const c = chartRef.current
    if (!c) return
    const o = c.getOverlayById(id)
    if (!o) return
    const ed = (o.extendData ?? {}) as Record<string, unknown>
    if (typeof ed.ctxId !== 'string') {
      c.overrideOverlay({ id, extendData: { ...ed, ctxId: `c-${nowMs().toString(36)}-${++ctxSeqRef.current}` } })
    }
  }

  const trackCreate = (id: string | null) => {
    if (id) {
      drawIdsRef.current.add(id)
      stampCtxId(id)
      notifyEdit() // drawings live in the engine, not React state
    }
  }

  const clearChartSelection = () => {
    if (selDotRef.current && chart) chart.removeOverlay(selDotRef.current)
    selDotRef.current = null
  }

  // Remove only tracked USER drawings — never a blanket removeOverlay(), which
  // would also wipe chrome overlays like the RSI reference bands.
  const clearUserDrawings = () => {
    drawIdsRef.current.forEach((id) => chart?.removeOverlay(id))
    drawIdsRef.current = new Set()
  }

  const removeAllDrawings = () => {
    if (!chart) return
    // LEAVE THE IDEA FIRST. Trade marks are drawings, so this wipe takes the
    // idea being edited with it — and the strip does not notice, because
    // `activeIdea` deliberately falls back to a descriptor when an idea has no
    // marks (that fallback is what keeps the strip up between starting an idea
    // and placing its entry). So an edit session survived its own idea's
    // deletion, leaving the strip, its note box and an armed step pointing at
    // nothing. `handleSymbolChange` — the other wipe — has always done this.
    // Teardown, not a save: this runs BEFORE clearUserDrawings, so the marks are
    // still there and a mark-count guard would read the wipe as finished work.
    resetIdeaEdit()
    clearUserDrawings()
    setSelected(null)
    notifyEdit()
  }

  const resetChartView = () => {
    if (!chart) return
    if (defaultBarSpaceRef.current) chart.setBarSpace(defaultBarSpaceRef.current)
    // After a walkthrough replay reloaded the canvas in the past, "the latest bar" is not loaded:
    // reload at the anchor rather than scroll to the end of an old window.
    if (series && series.getCutoff() === null && !series.reachesAnchor()) {
      void series.load({ symbol, timeframe, anchorTs })
      return
    }
    if (series) series.scrollToLatest()
    else chart.scrollToRealTime()
  }

  // ---- Indicators --------------------------------------------------------------
  const addIndicator = useCallback(
    (option: IndicatorOption) => {
      if (!chart) return
      if (option.volume) {
        setVolume((v) => ({ ...v, present: true, visible: true }))
        return
      }
      // Duplicates allowed: the engine addresses instances by (paneId, name)
      // only, so each instance needs a unique ENGINE name — the base name if
      // free, else a registered clone ("MA#2", …).
      setIndicators((prev) => {
        const engineName = mintEngineName(option.name, prev.map((i) => i.engineName))
        if (engineName !== option.name) {
          // Base taken ⇒ a live sibling exists to read the template off.
          const src = prev.find((i) => i.name === option.name)
          if (!src || !ensureIndicatorClone(chart, src, engineName, option.name)) return prev
        }
        const res = applyIndicator(chart, option, defaultSettings(), engineName)
        if (!res) return prev
        return [
          ...prev,
          { id: `i${++instCounterRef.current}`, name: option.name, engineName, paneId: res.paneId, bandIds: res.bandIds, settings: res.settings },
        ]
      })
    },
    [chart],
  )

  const removeIndicatorById = useCallback(
    (id: string) => {
      const inst = indicators.find((i) => i.id === id)
      if (!inst || !chart) return
      chart.removeIndicator(inst.paneId, inst.engineName)
      inst.bandIds.forEach((bid) => chart.removeOverlay(bid))
      setIndicators((prev) => prev.filter((i) => i.id !== id))
      if (settingsId === id) setSettingsId(null)
    },
    [chart, indicators, settingsId],
  )

  const toggleIndicatorVisible = useCallback(
    (id: string) => {
      setIndicators((prev) =>
        prev.map((i) => {
          if (i.id !== id) return i
          const visible = !(i.settings.visible !== false)
          if (chart) chart.overrideIndicator({ name: i.engineName, visible }, i.paneId)
          return { ...i, settings: { ...i.settings, visible } }
        }),
      )
    },
    [chart],
  )

  const updateInstanceSettings = useCallback(
    (id: string, patch: Partial<IndicatorInstance['settings']>) => {
      setIndicators((prev) =>
        prev.map((i) => {
          if (i.id !== id) return i
          const next = { ...i, settings: { ...i.settings, ...patch } }
          if (chart) updateIndicatorStyles(chart, next)
          return next
        }),
      )
    },
    [chart],
  )

  const removeAllIndicators = () => {
    if (!chart) return
    indicators.forEach((inst) => {
      chart.removeIndicator(inst.paneId, inst.engineName)
      inst.bandIds.forEach((id) => chart.removeOverlay(id))
    })
    setIndicators([])
    setSettingsId(null)
  }

  const removeVolume = () => setVolume((v) => ({ ...v, present: false }))
  const removeAll = () => {
    removeAllDrawings()
    removeAllIndicators()
    removeVolume()
  }

  /**
   * Remove all asks first, and makes the trader type the words.
   *
   * It is the largest irreversible act on this chart — every drawing, every
   * trade idea with its reasoning, every indicator, all from one item in a
   * menu — and there is no undo behind it. Gated like the trade-idea delete,
   * with a longer word to type because it takes more.
   *
   * The body counts what goes and names what stays: a warning that
   * over-promises destruction is as misleading as one that under-promises it,
   * and teaches the trader that these warnings exaggerate.
   */
  const confirmRemoveAll = () => {
    const ideaCount = ideas.length
    // Trade idea marks ARE tracked drawings, so they are taken OUT of the
    // drawing count and named on their own line. Left in, the two numbers read
    // as separate piles — "4 drawings and 1 trade idea" when there are four
    // marks and nothing else — which overstates what is on the chart.
    const inIdeas = ideas.reduce((n, i) => n + i.marks.length, 0)
    const draws = Math.max(0, drawIdsRef.current.size - inIdeas)
    const inds = indicators.length
    const parts: string[] = []
    if (draws) parts.push(`${draws} ${draws === 1 ? 'drawing' : 'drawings'}`)
    if (ideaCount) parts.push(`${ideaCount} ${ideaCount === 1 ? 'trade idea' : 'trade ideas'}`)
    if (inds) parts.push(`${inds} ${inds === 1 ? 'indicator' : 'indicators'}`)
    if (volume.present) parts.push('the volume')
    const list = parts.length
      ? parts.length > 1
        ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
        : parts[0]
      : 'everything'
    setConfirmAsk({
      open: true,
      title: 'Remove everything?',
      body: `This clears ${list} from the chart${
        ideaCount ? ', including the reasoning written on every level' : ''
      }. Your on-chart text and callouts stay. This can't be undone.`,
      confirmLabel: 'Yes, delete all',
      typeToConfirm: 'delete all',
      action: removeAll,
    })
  }

  // Volume: create / restyle / hide / remove the VOL_OVERLAY from `volume` state.
  // Colours fall back to the theme default when not customised.
  useEffect(() => {
    if (!chart) return
    const exists = !!getIndicator(chart, 'candle_pane', VOLUME_INDICATOR)
    if (!volume.present) {
      if (exists) chart.removeIndicator('candle_pane', VOLUME_INDICATOR)
      return
    }
    const cols = { up: volume.up ?? volColors(isDark).up, down: volume.down ?? volColors(isDark).down }
    if (!exists) {
      chart.createIndicator(
        { name: VOLUME_INDICATOR, extendData: cols, createTooltipDataSource: () => ({ name: '', calcParamsText: '', icons: [], values: [] }) },
        true,
        { id: 'candle_pane' },
      )
    }
    chart.overrideIndicator({ name: VOLUME_INDICATOR, visible: volume.visible !== false, extendData: cols }, 'candle_pane')
  }, [chart, volume, isDark])

  // ---- Overlay events (spread into every createOverlay) ------------------------
  // Shared by onSelected and onClick: open the floating toolbar for a drawing.
  const selectFromEvent = (e: { overlay: { id: string; name: string; extendData?: unknown }; x?: number; y?: number }) => {
    // The drawing still being placed is not a pick. The engine reports presses
    // on it between points, and a toolbar opened then sits over the chart —
    // on a phone, right where the channel's third tap has to land.
    if (progressIdRef.current && e.overlay.id === progressIdRef.current) return
    // A trade level is opened by CLICKING it — never by hovering. The click
    // brings up its idea and its note in one gesture, which is the only way in
    // from the chart now that these levels carry no hover card.
    if (isTradeMark(e.overlay.name)) {
      // While a step is armed the trader is PLACING, not inspecting. The click
      // belongs to the level being marked even when it lands on an existing
      // line — a level spans the whole chart width, so with several ideas up
      // there is often one under the pointer, and silently switching to it
      // instead of placing would be baffling.
      if (armedStepRef.current) return
      const edx = (e.overlay.extendData ?? {}) as Record<string, unknown>
      const mid = edx.ideaId
      if (typeof mid === 'string' && mid !== ideaEditRef.current?.ideaId) enterIdeaEdit(mid, false)
      const role = roleOfSlot(typeof edx.slot === 'string' ? edx.slot : undefined)
      // onClick and onSelected can both fire for one press; the guard keeps that
      // from reopening (and so re-seeding) a box the trader is already typing in.
      if (role && ideaNoteRef.current?.markId !== e.overlay.id) {
        openIdeaNote(e.overlay.id, role, e.x ?? 0, e.y ?? 0, typeof edx.note === 'string' ? edx.note : '')
      }
      return
    }
    clearChartSelection()
    // A fresh pick gets its comment box back, even on the drawing whose box was
    // just closed with its ×: that × dismissed one SELECTION's box, and this is
    // a new one. Without this, closing the box once would hide it on that
    // drawing for good, since the dismissal is keyed by the overlay id.
    setNoteDismissedKey(null)
    const ed = (e.overlay.extendData ?? {}) as { extend?: string; levels?: SelectedDrawing['levels']; labelMode?: 'values' | 'percent'; fillColor?: string; note?: string }
    setSelected({
      id: e.overlay.id,
      key: e.overlay.id, // stable per selection; preserved across extend swaps
      name: e.overlay.name,
      extend: ed.extend,
      levels: ed.levels,
      labelMode: ed.labelMode,
      fillColor: ed.fillColor,
      note: typeof ed.note === 'string' ? ed.note : '',
      x: e.x ?? 0,
      y: e.y ?? 0,
      paneW: chartWrapRef.current?.clientWidth ?? 800,
    })
  }

  /**
   * Select a drawing the trader did not click — specifically, the one they have
   * just finished drawing.
   *
   * A finished drawing used to leave nothing selected, so its editing toolbar
   * and its comment box only appeared if the trader went back and clicked it.
   * Both windows belong to the shape the moment it exists: the toolbar because
   * the look is what you adjust first, the comment box because the reason you
   * drew it is freshest right now.
   *
   * Anchored on the LAST point — where the draw ended, and so where the pointer
   * already is — rather than the first.
   */
  const selectDrawing = (id: string, fresh = false) => {
    const c = chartRef.current
    const o = c?.getOverlayById(id)
    if (!c || !o?.points?.length) return
    const last = o.points[o.points.length - 1]
    const px = asPixel(c.convertToPixel(last, { paneId: 'candle_pane', absolute: true }))
    if (!px) return
    const ed = (o.extendData ?? {}) as {
      extend?: string
      levels?: SelectedDrawing['levels']
      labelMode?: 'values' | 'percent'
      fillColor?: string
      note?: string
    }
    setSelected({
      id,
      key: id,
      name: o.name,
      extend: ed.extend,
      levels: ed.levels,
      labelMode: ed.labelMode,
      fillColor: ed.fillColor,
      note: typeof ed.note === 'string' ? ed.note : '',
      fresh,
      x: px.x,
      y: px.y,
      paneW: chartWrapRef.current?.clientWidth ?? 800,
    })
  }

  /**
   * A drawing's draw just ended: track it, put the tool down, and select it so
   * its two windows come up. Flagged `fresh`, which is what lets the comment box
   * take the keyboard for this one selection and no other.
   */
  const finishDrawing = (id: string | null) => {
    trackCreate(id)
    endDrawing()
    if (!id) return
    setNoteDismissedKey(null)
    selectDrawing(id, true)
  }

  // Entry/exit setups span three engine overlays (entry marker, exit marker,
  // dashed connector) joined only by extendData.setupId — the one link that
  // survives save/restore. Resolve the trio for a given setupId.
  const setupOverlays = (setupId: string) => {
    const found: { entry?: EngineOverlay; exit?: EngineOverlay; connector?: EngineOverlay } = {}
    if (!chart) return found
    for (const id of drawIdsRef.current) {
      const o = chart.getOverlayById(id)
      if (!o) continue
      const ed = (o.extendData ?? {}) as Record<string, unknown>
      if (ed.setupId !== setupId) continue
      if (ed.connector === true) found.connector = o
      else if (o.name === 'exitPoint') found.exit = o
      else if (SETUP_MARKERS.includes(o.name)) found.entry = o
    }
    return found
  }

  const plainPt = (p: Partial<Point>): Partial<Point> => ({ timestamp: p.timestamp, dataIndex: p.dataIndex, value: p.value })

  // Re-anchor the dashed connector to its setup markers' LIVE positions — the
  // engine moves markers natively, so a drag never touches the connector itself.
  const syncConnector = (moved: { name: string; extendData?: unknown }) => {
    if (!chart || !SETUP_MARKERS.includes(moved.name)) return
    const setupId = ((moved.extendData ?? {}) as Record<string, unknown>).setupId
    if (typeof setupId !== 'string') return
    const { entry, exit, connector } = setupOverlays(setupId)
    const a = entry?.points?.[0]
    const b = exit?.points?.[0]
    if (!connector || !a || !b) return
    chart.overrideOverlay({ id: connector.id, points: [plainPt(a), plainPt(b)] })
  }

  const overlayEvents: Pick<
    OverlayCreate,
    'onClick' | 'onSelected' | 'onDeselected' | 'onRightClick' | 'onMouseEnter' | 'onMouseLeave' | 'onPressedMoveStart' | 'onPressedMoving' | 'onPressedMoveEnd'
  > = {
    // The engine fires onSelected only when the clicked overlay CHANGES; after
    // a scroll-deselect (or a click on an already-selected drawing) onClick is
    // the only signal — reopen the toolbar from it.
    onClick: (e) => {
      // An armed pin tool takes the press: it is pinning to this drawing, not editing it.
      if (isPinTool(activeToolRef.current)) {
        pinPickRef.current = e.overlay.id
        return false
      }
      drawingClickRef.current = true // suppress the candle/indicator selection dot
      // Pressing the shape means "I want to work on this shape", and that
      // includes what you were saying about it. Needed on the ALREADY-selected
      // drawing too, which is why it sits above the guard below: an empty-chart
      // press closes the comment box without deselecting the drawing (the
      // engine keeps the selection), so without this the box could only be
      // brought back by picking some other drawing first.
      //
      // The note is RE-READ here, not just un-hidden. The selection carries a
      // snapshot of the sentence taken when it was made, and the guard below
      // means a press on the already-selected drawing never refreshes it — so
      // the box would reopen showing the note as it was BEFORE the trader wrote
      // it, and then save that back over what they had written.
      if (isNotableDrawing(e.overlay.name)) {
        setNoteDismissedKey(null)
        const edx = (e.overlay.extendData ?? {}) as { note?: unknown }
        const note = typeof edx.note === 'string' ? edx.note : ''
        // `fresh` goes with it: this is a drawing being picked up, not one just
        // finished, so it must not pull the keyboard up.
        setSelected((s) => (s && s.id === e.overlay.id ? { ...s, note, fresh: false } : s))
      }
      if (selectedRef.current?.id !== e.overlay.id) selectFromEvent(e)
      return false
    },
    onSelected: (e) => {
      if (isPinTool(activeToolRef.current)) {
        pinPickRef.current = e.overlay.id
        return false
      }
      drawingClickRef.current = true // suppress the candle/indicator selection dot
      selectFromEvent(e)
      return false
    },
    onDeselected: () => {
      setSelected(null)
      return false
    },
    // Suppress klinecharts' default right-click-deletes-the-drawing (an easy
    // accidental loss). Clone/Delete live on the floating toolbar instead.
    onRightClick: () => true,
    onMouseEnter: () => {
      if (isPinTool(activeToolRef.current)) return false // the cursor stays a crosshair
      setChartCursor('pointer')
      return false
    },
    onMouseLeave: () => {
      if (isPinTool(activeToolRef.current)) return false
      setChartCursor('')
      return false
    },
    // Press on a placed drawing. Grabbing a corner/handle RESIZES (magnet applies,
    // Cmd inverts it); grabbing the BODY MOVES (base magnet only).
    onPressedMoveStart: (e) => {
      // An armed pin tool: the press is a pick, never a drag (onPressedMoving refuses the move).
      if (isPinTool(activeToolRef.current)) return false
      setChartCursor('grabbing')
      pressedIdRef.current = e.overlay.id
      setEditing(true)
      // Remember where a trade level started, so an illegal drop can be undone.
      preDragRef.current = isTradeMark(e.overlay.name)
        ? { id: e.overlay.id, value: (e.overlay as { points?: Array<{ value?: number }> }).points?.[0]?.value ?? Number.NaN }
        : null
      // Leg markers are single-point: treat ANY press as a body move (never a resize).
      const isLeg = LEG_MARKERS.includes(e.overlay.name)
      const isHandle = !isLeg && (e.figureKey ?? '').startsWith('overlay_figure_point_')
      pressedIsHandleRef.current = isHandle
      const invert = isHandle ? magnetRef.current !== cmdHeldRef.current : magnetRef.current
      chart?.overrideOverlay({ id: e.overlay.id, mode: invert ? 'strong_magnet' : 'normal' } as Partial<OverlayCreate>)
      return false
    },
    onPressedMoving: (e) => {
      // True cancels the move in klinecharts — the drawing stays exactly where it is.
      if (isPinTool(activeToolRef.current)) return true
      syncConnector(e.overlay) // connector follows the marker live during the drag
      // klinecharts moves the overlay only after this callback returns, so read the
      // level once it has moved; otherwise the readout trails the line by one step.
      queueMicrotask(() => showDragReadout(e))
      return false
    },
    onPressedMoveEnd: (e) => {
      if (isPinTool(activeToolRef.current)) return false
      // THE HARD LOCK, on release. Put it back rather than clamping it to the
      // entry — and say nothing: the snap-back IS the sentence, delivered by
      // the thing they still have hold of.
      if (isTradeMark(e.overlay.name)) revertIllegalDrag(e.overlay.id)
      setDragGuide(null)
      syncConnector(e.overlay) // exact final geometry, whatever the moving-event ordering
      setChartCursor('pointer')
      lastManipEndRef.current = nowMs() // the trailing synthetic click must not read as click-away
      pressedIdRef.current = null
      pressedIsHandleRef.current = false
      setEditing(false)
      notifyEdit() // geometry moved/resized
      return false
    },
  }

  // Cursor feedback like TV: hover a drawing → pointer; hold to drag → grabbing.
  const setChartCursor = (c: string) => {
    const el = chartWrapRef.current
    if (!el) return
    el.style.cursor = c
    el.querySelectorAll('canvas').forEach((cv) => {
      cv.style.cursor = c
    })
  }

  // ---- Tool arming / drawing ----------------------------------------------------
  const setDrawLock = (lock: boolean) => {
    chart?.setScrollEnabled(!lock)
    chart?.setZoomEnabled(!lock)
  }

  const endDrawing = () => {
    setActiveTool('cursor')
    activeToolRef.current = null
    progressIdRef.current = null
    pinPickRef.current = null
    setPinGuide(null)
    // A finger that finishes a drawing has usually wobbled a few pixels, and on
    // touchend the engine turns that into a fling. Unlocked now, the fling pans
    // the chart, and the pan drops the fresh selection, note and all. So stay
    // locked until the NEXT press starts: pointerdown fires before the engine's
    // touchstart, which cancels any fling, so that gesture pans as normal.
    if (deferredUnlockRef.current) window.removeEventListener('pointerdown', deferredUnlockRef.current, true)
    deferredUnlockRef.current = null
    if (lastPressTouchRef.current) {
      const unlock = () => {
        window.removeEventListener('pointerdown', unlock, true)
        deferredUnlockRef.current = null
        if (!activeToolRef.current?.overlay) setDrawLock(false)
      }
      deferredUnlockRef.current = unlock
      window.addEventListener('pointerdown', unlock, true)
      return
    }
    setDrawLock(false)
  }

  // Shared create config so click-draw and drag-draw produce identical overlays,
  // seeded with the last-used look for the drawing type.
  const overlayConfig = (overlayName: string): Partial<OverlayCreate> => {
    const mem = drawStyleRef.current[overlayName]
    if (overlayName === 'fibRetracement') {
      const med = mem?.extendData ?? {}
      return {
        styles: {},
        extendData: {
          levels: med.levels ?? DEFAULT_FIB_LEVELS,
          extend: 'none',
          labelMode: med.labelMode ?? 'values',
          fillColor: med.fillColor,
          lineSize: med.lineSize,
          precision: chart?.getPriceVolumePrecision().price ?? 5,
        },
      }
    }
    if (overlayName === 'rectangle') return mem?.polygon ? { styles: { polygon: mem.polygon } } : { styles: {} }
    return { styles: { line: mem?.line ?? { color: DEFAULT_DRAW_COLOR } } }
  }

  const handleSelectTool = (tool: ToolDef) => {
    // Abort any in-progress draw from the previous tool.
    if (chart && progressIdRef.current) {
      chart.removeOverlay(progressIdRef.current)
      progressIdRef.current = null
    }
    if (chart && previewIdRef.current) {
      chart.removeOverlay(previewIdRef.current)
      previewIdRef.current = null
    }
    // The Trade ideas list is a docked view, not a tool: it must not steal the
    // armed state (nor clear an idea half-placed).
    if (tool.panel) {
      setIdeaPanelOpen((v) => !v)
      return
    }
    // Picking any other tool leaves edit mode — leaving IS saving, the marks
    // are already on the chart.
    if (ideaEditRef.current) exitIdeaEdit()
    clearChartSelection()
    setActiveTool(tool.id)
    activeToolRef.current = tool
    if (tool.idea) {
      startIdea(tool.idea)
      return
    }
    if (isPinTool(tool)) {
      // Everything that could be edited is put down first: while armed, the next
      // press pins a note and must never open a drawing instead.
      setSelected(null)
      setSelectedNoteId(null)
      setHoverNote(null)
      setComposer(null)
      pinPickRef.current = null
      setDrawLock(false) // pan and zoom stay live, as for the other click-to-place tools
      return
    }
    if (tool.note) {
      // Click-to-place tools keep pan/zoom LIVE (TradingView-style): drag pans,
      // wheel zooms, a plain click places. onChartClick's press-distance guard
      // keeps a pan's trailing click from placing anything.
      setDrawLock(false)
      return
    }
    if (!chart || !tool.overlay) {
      progressIdRef.current = null
      setDrawLock(false) // cursor
      return
    }
    setDrawLock(true)
    // Engine click-draw (click-move-click). Magnet snaps points to OHLC while
    // drawing via the overlay mode; reset to normal on finish so edits don't snap.
    progressIdRef.current = asOverlayId(
      chart.createOverlay({
        name: tool.overlay,
        mode: (magnet ? 'strong_magnet' : 'normal') as OverlayCreate['mode'],
        ...overlayConfig(tool.overlay),
        onDrawEnd: (e) => {
          chart.overrideOverlay({ id: e.overlay.id, mode: 'normal' } as Partial<OverlayCreate>)
          finishDrawing(e.overlay.id)
          return true
        },
        ...overlayEvents,
      }),
    )
  }

  // ---- Drag-to-draw ---------------------------------------------------------------
  const pointAt = (e: { clientX: number; clientY: number; metaKey?: boolean; ctrlKey?: boolean }): Partial<Point> | null => {
    const rect = chartWrapRef.current?.getBoundingClientRect()
    if (!rect || !chart) return null
    const p = asPoint(chart.convertFromPixel([{ x: e.clientX - rect.left, y: e.clientY - rect.top }], { paneId: 'candle_pane', absolute: true }))
    // Magnet: snap to nearest OHLC. Cmd/Ctrl inverts the toggle while drawing.
    const useMagnet = magnet !== Boolean(e.metaKey || e.ctrlKey)
    if (p && useMagnet && p.dataIndex != null && p.value != null) {
      const candle = chart.getDataList()[p.dataIndex]
      if (candle) {
        p.value = [candle.open, candle.high, candle.low, candle.close].reduce((a, b) =>
          Math.abs(b - (p.value as number)) < Math.abs(a - (p.value as number)) ? b : a,
        )
      }
    }
    return p
  }

  // Build the points a drag produces for a given tool (channel needs a 3rd point).
  const buildDragPoints = (overlayName: string | null | undefined, a: Partial<Point>, b: Partial<Point>): Array<Partial<Point>> => {
    if (overlayName === 'parallelStraightLine') {
      const av = a.value ?? 0
      const bv = b.value ?? 0
      const offset = Math.abs(av) * 0.003 || Math.abs(bv - av) || 1
      return [a, b, { timestamp: b.timestamp, dataIndex: b.dataIndex, value: bv + offset }]
    }
    return [a, b]
  }

  const onDrawPointerDown = (e: React.PointerEvent) => {
    // Every chart-area press is recorded so onChartClick can tell a genuine
    // click from the trailing click a pan-drag fires on release.
    sawMultiTouchRef.current = activePointersRef.current > 0
    activePointersRef.current++
    const touch = e.pointerType === 'touch'
    lastPressTouchRef.current = touch
    pressPtRef.current = { x: e.clientX, y: e.clientY, t: nowMs(), touch }
    if (activeTool === 'cursor' || !activeToolRef.current?.overlay) return
    drawRef.current = { x: e.clientX, y: e.clientY, pt: pointAt(e), dragging: false, touch }
  }

  const onDrawPointerMove = (e: React.PointerEvent) => {
    const st = drawRef.current
    if (!st.pt || !(e.buttons & 1) || !chart) return
    const tool = activeToolRef.current
    if (!tool?.overlay || !DRAG_TOOLS.includes(tool.overlay)) return
    if (!st.dragging && Math.hypot(e.clientX - st.x, e.clientY - st.y) <= tapSlop(st.touch)) return
    const cur = pointAt(e)
    if (!cur) return
    if (!st.dragging) {
      // drag begins: drop the engine's click-draw, start a live preview overlay
      st.dragging = true
      if (progressIdRef.current) {
        chart.removeOverlay(progressIdRef.current)
        progressIdRef.current = null
      }
      previewIdRef.current = asOverlayId(
        chart.createOverlay({
          name: tool.overlay,
          points: buildDragPoints(tool.overlay, st.pt, cur),
          ...overlayConfig(tool.overlay),
          onDrawEnd: () => true,
          ...overlayEvents,
        }),
      )
    } else if (previewIdRef.current) {
      chart.overrideOverlay({ id: previewIdRef.current, points: buildDragPoints(tool.overlay, st.pt, cur) })
    }
  }

  const onDrawPointerUp = (e: React.PointerEvent) => {
    const st = drawRef.current
    drawRef.current = { x: 0, y: 0, pt: null, dragging: false, touch: false }
    if (!st.dragging || !previewIdRef.current || !chart || !st.pt) return // click → engine handles it
    const upPt = pointAt(e)
    if (upPt) {
      chart.overrideOverlay({ id: previewIdRef.current, points: buildDragPoints(activeToolRef.current?.overlay, st.pt, upPt) })
    }
    const drawnId = previewIdRef.current
    previewIdRef.current = null
    finishDrawing(drawnId)
  }

  // klinecharts preventDefaults touchend on every tap (its own tap pipeline),
  // which suppresses the browser's compatibility `click` — so on touch the
  // wrapper's onClick never fires. Recreate the click routing from clean taps.
  const onTouchTapUp = (e: React.PointerEvent) => {
    activePointersRef.current = Math.max(0, activePointersRef.current - 1)
    if (e.pointerType !== 'touch') return
    const press = pressPtRef.current
    if (!press || sawMultiTouchRef.current) return
    if (Math.hypot(e.clientX - press.x, e.clientY - press.y) > tapSlop(true)) return
    if (nowMs() - press.t >= 450) return // long-press means crosshair, not placement
    lastTouchTapRef.current = nowMs()
    // Defer one macrotask: pointerup precedes touchend, and the engine
    // dispatches marker onClick (which sets drawingClickRef) during touchend —
    // the routing must observe it so tapping a marker selects, not places.
    setTimeout(() => onChartClick(e), 0)
  }

  // The idea being edited. Falls back to the descriptor while it has no marks.
  const activeIdea: TradeIdea | null = ideaEdit
    ? (ideas.find((i) => i.ideaId === ideaEdit.ideaId) ?? {
        ideaId: ideaEdit.ideaId,
        seq: ideaEdit.seq,
        name: ideaName(ideaEdit.seq),
        color: ideaEdit.color,
        dir: ideaEdit.dir,
        marks: [],
        entry: null,
        stop: null,
        takeProfits: [],
      })
    : null

  /**
   * Every idea currently on the chart. extendData is already persisted verbatim,
   * so an idea needs no state of its own — it IS the marks.
   *
   * Reads refs, so it may only be called from handlers and effects; notifyEdit
   * pushes the result into state for rendering.
   */
  /**
   * On a narrow screen a placed stop loss or take profit being moved reads out at the top, as
   * when it was placed: in the strip's row when its idea is the one being edited, otherwise in
   * a bar of its own in the strip's place, there only while the level is held.
   */
  const dragInStrip = topReadout && !!dragGuide && !!activeIdea && dragGuide.ideaId === activeIdea.ideaId
  const dragOnTop = topReadout && !!dragGuide && !dragInStrip

  const readIdeas = (): TradeIdea[] => {
    const c = chartRef.current
    if (!c) return []
    const marks: TradeIdeaMark[] = []
    drawIdsRef.current.forEach((id) => {
      const o = c.getOverlayById(id)
      if (!o) return
      const m = toTradeMark(id, o.name, o.extendData, o.points?.[0])
      if (m) marks.push(m)
    })
    return collectTradeIdeas(marks)
  }

  const liveIdea = (ideaId: string | null | undefined): TradeIdea | null =>
    ideaId ? (readIdeas().find((i) => i.ideaId === ideaId) ?? null) : null

  /**
   * The user just left their FIRST EVER trade idea on this strategy: on a phone the
   * chat opens itself onto the composer's "Send chart to AI" tab, so they learn the
   * job is finished by talking to the AI. `chartActivity` owns "once ever" — every
   * later idea, and every other drawing tool, is silent.
   */
  const nudgeOnFirstIdea = () => {
    // An idea with no marks was never placed (Escape straight out of BUY), and a
    // deleted one has none left. Neither is a save. Read before the reset, while
    // ideaEditRef still points at the outgoing idea.
    if ((liveIdea(ideaEditRef.current?.ideaId)?.marks.length ?? 0) === 0) return
    chartActivity.markIdeaSaved(bid)
  }

  /**
   * Paint exactly one idea loud, by writing `flag` on its marks and clearing it
   * everywhere else. Only where the flag actually changes — an overrideOverlay
   * per mark per pointermove would repaint the canvas on every mouse event.
   */
  const stampIdeaFlag = (flag: 'active' | 'lineFocus', ideaId: string | null) => {
    const c = chartRef.current
    if (!c) return
    drawIdsRef.current.forEach((id) => {
      const o = c.getOverlayById(id)
      if (!o || !isTradeMark(o.name)) return
      const edx = (o.extendData ?? {}) as Record<string, unknown>
      const want = ideaId != null && edx.ideaId === ideaId
      if ((edx[flag] === true) === want) return
      c.overrideOverlay({ id, extendData: { ...edx, [flag]: want } })
    })
  }

  const stampActive = (ideaId: string | null) => stampIdeaFlag('active', ideaId)
  /** Hovering any level of an idea lights the whole idea. */
  const stampLineFocus = (ideaId: string | null) => stampIdeaFlag('lineFocus', ideaId)

  /**
   * Pure teardown: drop the edit session and everything hanging off it. Used
   * directly wherever the canvas is being wiped or suspended UNDER the idea —
   * that is not the user leaving their work, so it must not read as a save.
   */
  const resetIdeaEdit = () => {
    setIdeaEdit(null)
    setArmedStep(null)
    armedStepRef.current = null
    setIdeaGuide(null)
    // Leaving the idea takes any open note box with it, and drops the baton —
    // there is no next step once there is no idea being edited.
    setIdeaNote(null)
    ideaNoteRef.current = null
    pendingNextRef.current = null
    // Pressing a control on the strip while a note box is open runs the box's
    // click-away, which arms the swallow to eat that gesture's trailing chart
    // click — but the gesture ended on the strip, so no such click is coming and
    // the flag would sit there waiting to eat the trader's NEXT one. Nothing
    // that lands here is owed a swallow: `finish` and `fromClickAway` are never
    // set together.
    swallowChartClickRef.current = false
    stampActive(null)
    endDrawing()
  }

  /**
   * THE USER leaving their idea, which IS the save — the marks were persisted the
   * moment they landed. The strip's Save, its ×, Escape and picking another tool
   * are the same act at four volumes, and all four arrive here.
   */
  /**
   * The phone's stand-in for Escape (PROD-3131). On the entry it drops the idea, since
   * nothing is placed yet; on a level it passes on it: the stop loss hands on to the take
   * profit, and the take profit, the last step, finishes the idea.
   */
  const skipArmedStep = (step: IdeaRole) => {
    setIdeaGuide(null)
    if (step === 'sl') {
      setArmedStep('tp')
      armedStepRef.current = 'tp'
      return
    }
    exitIdeaEdit()
  }

  const exitIdeaEdit = () => {
    nudgeOnFirstIdea()
    resetIdeaEdit()
  }

  /** BUY / SELL on the rail: a fresh idea, armed on its entry. */
  const startIdea = (dir: 'buy' | 'sell') => {
    const seq = ideaSeqRef.current++
    const next = {
      ideaId: `ti-${nowMs().toString(36)}-${seq}`,
      seq,
      color: ideaColor(seq),
      dir,
    }
    setIdeaEdit(next)
    ideaEditRef.current = next
    setArmedStep('entry')
    armedStepRef.current = 'entry'
    stampActive(next.ideaId)
    setIdeaPanelOpen(false)
    clearChartSelection()
    // Click-to-place keeps pan/zoom live, like the note tools.
    setDrawLock(false)
  }

  /**
   * Open an existing idea and bring it into view.
   *
   * `arm` separates the two ways in. From the PANEL it means "work on this
   * idea", so whatever it still needs is armed and the next click continues
   * building. From a LEVEL ON THE CHART it means "this level" — that click
   * opens the level's note, and arming as well would make the following click
   * plant something instead of opening the next level.
   *
   * Either way a FINISHED idea arms nothing — `pendingStep` answers null — so
   * no click on the chart turns into another take profit. Adding one is
   * deliberate: the strip's Take profit step, or the note box's "Add another
   * take profit".
   */
  const enterIdeaEdit = (ideaId: string, arm = true) => {
    const idea = liveIdea(ideaId)
    if (!idea) return
    const next = { ideaId, seq: idea.seq, color: idea.color, dir: idea.dir }
    setIdeaEdit(next)
    ideaEditRef.current = next
    const step = arm ? pendingStep(idea) : null
    setArmedStep(step)
    armedStepRef.current = step
    stampActive(ideaId)
    setIdeaPanelOpen(false)
    setDrawLock(false)
  }

  /**
   * Bring an idea into view — and only when it needs bringing.
   *
   * Not `scrollToTimestamp`, which parks its target at the RIGHT EDGE: aiming
   * it at the entry pushed the stop and the targets (placed later in time) off
   * the screen, so the gesture meant to show you an idea hid most of it. This
   * centres the idea's own span instead, and leaves the chart alone when it is
   * already on screen — a view that jumps when it did not need to is its own
   * kind of lost.
   */
  const revealIdea = (idea: TradeIdea | null) => {
    const c = chartRef.current
    const stamps = (idea?.marks ?? []).map((m) => m.timestamp).filter((t) => t > 0)
    if (!c || !stamps.length) return
    const win = visibleWindow(c)
    if (!win) return
    const lo = Math.min(...stamps)
    const hi = Math.max(...stamps)
    if (lo >= win.fromTs && hi <= win.toTs) return // already looking at it

    const list = c.getDataList()
    if (!list.length) return
    const mid = (lo + hi) / 2
    let idx = 0
    let best = Infinity
    list.forEach((b, i) => {
      const d = Math.abs(b.timestamp - mid)
      if (d < best) {
        best = d
        idx = i
      }
    })
    // The target lands at the right edge, so aim half a screen past the idea's
    // middle to leave the whole of it centred.
    const vr = c.getVisibleRange()
    c.scrollToDataIndex(idx + Math.max(1, Math.round((vr.to - vr.from) / 2)))
  }

  /** The panel's way in: open the idea AND go and look at it. */
  const openIdeaFromPanel = (ideaId: string) => {
    enterIdeaEdit(ideaId)
    revealIdea(liveIdea(ideaId))
  }

  /**
   * The panel's way into ONE LEVEL: look at the idea, edit it, and open that
   * level's note.
   *
   * The same three acts a click on the level itself performs (`openLevelUnder`),
   * plus the reveal the panel owes — the row is a list entry, not a thing on
   * screen, so nothing guarantees the level is in view when it is pressed.
   *
   * `arm: false` for the reason the chart route passes it: the trader named THIS
   * level, and arming the idea's next step would turn their following click into
   * a placement instead of another opening.
   *
   * Hidden levels open too. A level off the canvas is still in the plan and its
   * note still reaches the AI, so the note stays reachable from the one place
   * that still lists it — the chart route skips hidden levels only because a
   * pointer cannot land on what is not drawn.
   */
  const openMarkFromPanel = (markId: string) => {
    const c = chartRef.current
    const o = c?.getOverlayById(markId)
    if (!c || !o) return
    const edx = (o.extendData ?? {}) as Record<string, unknown>
    const ideaId = typeof edx.ideaId === 'string' ? edx.ideaId : null
    const role = roleOfSlot(typeof edx.slot === 'string' ? edx.slot : undefined)
    if (!ideaId || !role) return

    enterIdeaEdit(ideaId, false)
    revealIdea(liveIdea(ideaId))

    // Where the box goes. A click brings its own pointer; a list row brings
    // none, so the level's own pixel stands in for one and the box opens on the
    // line it is about. Read AFTER the reveal so a scrolled view is the one
    // measured, and only the x can go stale anyway — price is vertical, and the
    // reveal only ever pans. The box clamps itself into the pane and below the
    // strip on open, so a stale x cannot put it out of reach.
    const px = asPixel(c.convertToPixel(o.points, { paneId: 'candle_pane', absolute: true }))
    const wrap = chartWrapRef.current
    openIdeaNote(
      markId,
      role,
      px?.x ?? Math.round((wrap?.clientWidth ?? 600) / 3),
      px?.y ?? Math.round((wrap?.clientHeight ?? 400) / 2),
      typeof edx.note === 'string' ? edx.note : '',
    )
  }

  /**
   * Deleting a trade idea asks first, and makes the trader type the word.
   *
   * A trade idea is not a drawing: it is an entry, a stop, its targets and the
   * reasoning written beside each one, and there is no undo on this chart. One
   * mis-aimed click on a bin the size of an icon would take an hour's thinking
   * with it. So every route in — the strip's bin and the panel's row — lands
   * here, and the act itself is `performDeleteIdea`, reachable only through the
   * dialog's confirm.
   *
   * Typing "delete" rather than the idea's name is a deliberateness check, not
   * a comprehension one (the name is in the title and the body instead). It is
   * the prototype's call, and worth knowing what it costs: typing the name
   * proved you knew WHICH idea you were destroying.
   */
  const confirmDeleteIdea = (ideaId: string) => {
    const idea = liveIdea(ideaId)
    if (!idea) return
    const n = idea.marks.length
    setConfirmAsk({
      open: true,
      title: `Delete ${idea.name.toLowerCase()}?`,
      body: `This removes "${idea.name}" and everything in it${
        n ? ` — ${n} ${n === 1 ? 'mark' : 'marks'}` : ''
      }: entries, targets, stops and any notes you've written. Your drawings stay on the chart. This can't be undone.`,
      confirmLabel: 'Yes, delete',
      typeToConfirm: 'delete',
      action: () => performDeleteIdea(ideaId),
    })
  }

  const performDeleteIdea = (ideaId: string) => {
    const c = chartRef.current
    if (!c) return
    for (const id of [...drawIdsRef.current]) {
      const o = c.getOverlayById(id)
      if (!o || !isTradeMark(o.name)) continue
      if (((o.extendData ?? {}) as Record<string, unknown>).ideaId !== ideaId) continue
      c.removeOverlay(id)
      drawIdsRef.current.delete(id)
    }
    // Teardown: the idea the user was editing no longer exists.
    if (ideaEditRef.current?.ideaId === ideaId) resetIdeaEdit()
    setSelected(null)
    notifyEdit()
  }

  const setIdeaColor = (ideaId: string, color: string) => {
    const c = chartRef.current
    if (!c) return
    drawIdsRef.current.forEach((id) => {
      const o = c.getOverlayById(id)
      if (!o || !isTradeMark(o.name)) return
      const edx = (o.extendData ?? {}) as Record<string, unknown>
      if (edx.ideaId !== ideaId) return
      c.overrideOverlay({ id, extendData: { ...edx, ideaColor: color } })
    })
    if (ideaEditRef.current?.ideaId === ideaId) {
      const next = { ...ideaEditRef.current, color }
      setIdeaEdit(next)
      ideaEditRef.current = next
    }
    notifyEdit()
  }

  const setMarkHidden = (markId: string, hidden: boolean) => {
    const c = chartRef.current
    const o = c?.getOverlayById(markId)
    if (!c || !o) return
    c.overrideOverlay({ id: markId, extendData: { ...((o.extendData ?? {}) as Record<string, unknown>), hidden } })
    notifyEdit()
  }

  /**
   * While a stop loss or take profit is dragged, the same measurement the placement guide
   * shows rides the pointer: "Moving take profit · Profit 72.4 pips · 1:2.5 RR" (PROD-3131).
   */
  /** Why an entry dragged to `price` would be put back, or null while it is between its levels. */
  const entryRefusal = (idea: TradeIdea, price: number): string | null => {
    const { min, max } = entryDragBounds(idea)
    const buy = ideaDirection(idea) === 'buy'
    if (min != null && price < min) return buy ? 'Entry goes above your stop loss' : 'Entry goes above your take profit'
    if (max != null && price > max) return buy ? 'Entry goes below your take profit' : 'Entry goes below your stop loss'
    return null
  }

  const showDragReadout = (e: { overlay: { name: string; extendData?: unknown; points?: Array<{ value?: number }> }; x?: number; y?: number }) => {
    if (!isTradeMark(e.overlay.name)) return
    const edx = (e.overlay.extendData ?? {}) as Record<string, unknown>
    const role = roleOfSlot(typeof edx.slot === 'string' ? edx.slot : undefined)
    if (!role) return
    const idea = liveIdea(typeof edx.ideaId === 'string' ? edx.ideaId : null)
    const price = e.overlay.points?.[0]?.value
    if (!idea || price == null) return setDragGuide(null)
    // Past where it may go, the level is refused, not measured: the drop will put it back. A stop
    // or target crosses its entry; an entry leaves the room between its stop and its target.
    const refusal = role === 'entry' ? entryRefusal(idea, price) : geometryRefusal(ideaDirection(idea), role, price, idea.entry?.value ?? null)
    const readout = refusal
      ? null
      : dragReadout({
          role,
          price,
          entry: idea.entry?.value,
          stop: idea.stop?.value,
          tp: idea.takeProfits[0]?.value,
          pipSize: pipSizeRef.current,
        })
    setDragGuide(
      readout || refusal
        ? {
            x: e.x ?? 0,
            y: e.y ?? 0,
            role,
            readout,
            refusal,
            color: idea.color,
            ideaId: idea.ideaId,
            plotW: chart?.getDom('candle_pane', DomPosition.Main)?.clientWidth ?? 0,
          }
        : null,
    )
  }

  /**
   * A trade level was dropped: if the idea would end up inverted, put it back.
   *
   * Stops and targets are measured against the entry; the entry itself is
   * penned between them. Either way the setup that lands is always one the lock
   * would have allowed you to draw in the first place.
   */
  const revertIllegalDrag = (overlayId: string) => {
    const c = chartRef.current
    const prev = preDragRef.current
    preDragRef.current = null
    const o = c?.getOverlayById(overlayId)
    if (!c || !o || !prev || prev.id !== overlayId || !Number.isFinite(prev.value)) return
    const edx = (o.extendData ?? {}) as Record<string, unknown>
    const role = roleOfSlot(typeof edx.slot === 'string' ? edx.slot : undefined)
    const value = o.points?.[0]?.value
    if (!role || value == null) return
    const idea = liveIdea(typeof edx.ideaId === 'string' ? edx.ideaId : null)
    if (!idea) return

    let illegal: boolean
    if (role === 'entry') {
      const { min, max } = entryDragBounds(idea)
      illegal = (min != null && value < min) || (max != null && value > max)
    } else {
      illegal = geometryRefusal(ideaDirection(idea), role, value, idea.entry?.value ?? null) != null
    }
    if (!illegal) return
    c.overrideOverlay({ id: overlayId, points: [{ ...(o.points?.[0] ?? {}), value: prev.value }] })
  }

  /** Pane price at a wrapper-relative pixel. */
  const priceAtPixel = (x: number, y: number): number | undefined =>
    chartRef.current
      ? asPoint(chartRef.current.convertFromPixel([{ x, y }], { paneId: 'candle_pane', absolute: true }))?.value
      : undefined

  /** The refusal for a price under the pointer, or null when it may be placed. */
  const refusalFor = (role: IdeaRole, value: number | undefined): string | null => {
    const idea = liveIdea(ideaEditRef.current?.ideaId)
    if (value == null) return null
    const dir = idea ? ideaDirection(idea) : (ideaEditRef.current?.dir ?? 'buy')
    return geometryRefusal(dir, role, value, idea?.entry?.value ?? null)
  }

  // The crosshair guide: what to click, or why this spot won't take it. Said
  // continuously under the pointer, so a refused click is never a click that
  // silently did nothing.
  const onIdeaGuideMove = (e: React.PointerEvent) => {
    const role = armedStepRef.current
    if (!role || !ideaEditRef.current || composer || ideaNote) {
      setIdeaGuide((v) => (v == null ? v : null))
      return
    }
    const rect = chartWrapRef.current?.getBoundingClientRect()
    const px = { x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) }
    const pt = chart ? asPoint(chart.convertFromPixel([px], { paneId: 'candle_pane', absolute: true })) : null
    setIdeaGuide({ x: px.x, y: px.y, refusal: refusalFor(role, pt?.value), price: pt?.value })
  }

  /** Place the armed level. Returns true when the click was ours to consume. */
  const placeIdeaMark = (e: React.MouseEvent): boolean => {
    const role = armedStepRef.current
    const edit = ideaEditRef.current
    if (!role || !edit) return false
    const candleDom = chart ? paneDom(chart, 'candle_pane') : null
    if (!chart || !candleDom || !candleDom.contains(e.target as Node)) return false
    e.preventDefault()

    const rect = chartWrapRef.current?.getBoundingClientRect()
    const px = { x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) }
    const pt = asPoint(chart.convertFromPixel([px], { paneId: 'candle_pane', absolute: true }))
    if (!pt || pt.dataIndex == null || pt.value == null) return true

    // THE HARD LOCK. Refused placements consume the click and stay armed: the
    // crosshair has already said why, and re-arming would feel like a reset.
    // A touchscreen has no hover to have said it, so say it where the tap landed.
    const refusal = refusalFor(role, pt.value)
    if (refusal) {
      setIdeaGuide({ x: px.x, y: px.y, refusal, price: pt.value })
      return true
    }

    const idea = liveIdea(edit.ideaId)
    const slot = nextFreeSlot(idea, role)
    if (!slot) return true

    const dl = chart.getDataList()
    const ts = dl[pt.dataIndex]?.timestamp ?? pt.timestamp
    const p = prec()
    const dir = idea ? ideaDirection(idea) : edit.dir
    const label = markLabel(role, slot, dir)
    const id = asOverlayId(
      chart.createOverlay(
        {
          name: TRADE_MARK,
          points: [{ timestamp: ts, dataIndex: pt.dataIndex, value: pt.value }],
          extendData: {
            ideaId: edit.ideaId,
            ideaSeq: edit.seq,
            ideaColor: edit.color,
            // Stamped on EVERY mark, so a restored idea — even one whose entry
            // was later deleted — still knows which way it points.
            dir,
            role,
            slot,
            label,
            note: '',
            precision: p,
            active: true,
          },
          onDrawEnd: () => true,
          ...overlayEvents,
        },
        'candle_pane',
      ),
    )
    if (!id) return true
    trackCreate(id)

    // Ask why. The next step is held as a baton and armed when the box closes,
    // NOT here — see pendingNextRef. A landing that COMPLETED the idea leaves no
    // baton at all: there is nothing further to ask for, so closing the box
    // stops leading rather than arming one more take profit.
    openIdeaNote(id, role, px.x, px.y, '')
    pendingNextRef.current = pendingStep(liveIdea(edit.ideaId))
    setArmedStep(null)
    armedStepRef.current = null
    setIdeaGuide(null)
    return true
  }

  /** Open the note box for a level, positioned at the cursor. */
  const openIdeaNote = (markId: string, role: IdeaRole, x: number, y: number, initial: string) => {
    const next = { markId, role, x, y, initial }
    setIdeaNote(next)
    // Set eagerly: selectFromEvent can fire twice for one press, and the ref is
    // what stops the second firing from re-seeding a box being typed in.
    ideaNoteRef.current = next
    setHoverNote(null)
    // The box IS this level's controls; a floating toolbar behind it would be a
    // second, competing set.
    setSelected(null)
  }

  /**
   * THE ONE EXIT from a level's note box. ×, Escape, Enter, a click away and
   * every footer button all land here.
   *
   * The footer's extra job rides as a flag rather than a second callback: a
   * separate finish route is how this flow repeatedly came to persist the note
   * by a different path, or skip the arming on the way out.
   */
  const commitIdeaNote = (text: string, exit?: IdeaNoteExit) => {
    const open = ideaNoteRef.current
    setIdeaNote(null)
    if (!open) return

    // THE BIN, and it must stay above the save: the level is going, so its note
    // must not be written back into an overlay on its way out. The baton dies
    // with the level that set it — a trader who deletes what they just placed is
    // not asking for the next step, and arming one would make their following
    // click plant something.
    if (exit?.deleteMark) {
      pendingNextRef.current = null
      handleDelete(open.markId)
      return
    }

    saveOverlayNote(open.markId, text)

    // The dismissing click is still travelling; eat it so it closes the box and
    // nothing else. Set BEFORE arming, because arming is what makes a trailing
    // click dangerous.
    if (exit?.fromClickAway) swallowChartClickRef.current = true

    const baton = pendingNextRef.current
    pendingNextRef.current = null

    if (exit?.finish) {
      exitIdeaEdit()
      return
    }
    // Each button says what it wants; ×, Escape, Enter and a click away say
    // nothing and only spend the baton a fresh placement left. That is what
    // lets a box re-opened on an existing level leave the chart as it found it
    // — arm on a passive close and the NEXT click plants instead of opening.
    // `advance` asks the same forward-only lookup that labelled the button, so
    // pressing "Next" arms exactly the step the box was promising.
    const next = exit?.addAnother
      ? 'tp'
      : exit?.advance
        ? pendingStep(liveIdea(ideaEditRef.current?.ideaId ?? ''), open.role)
        : baton
    if (!next) return
    setArmedStep(next)
    armedStepRef.current = next
    // The guide appears under the pointer immediately rather than after the
    // first pixel of movement: a trader who dismissed the box and held still
    // would otherwise see an armed chart with nothing drawn on it.
    if (exit?.fromClickAway && Number.isFinite(exit.x)) {
      const r = chartWrapRef.current?.getBoundingClientRect()
      const cx = exit.x as number
      const cy = exit.y as number
      if (r && cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom) {
        const price = priceAtPixel(cx - r.left, cy - r.top)
        setIdeaGuide({ x: cx - r.left, y: cy - r.top, refusal: refusalFor(next, price), price })
      }
    }
  }

  /**
   * The drawing comment box's one exit.
   *
   * Deliberately does NOT set `swallowChartClickRef`, unlike `commitIdeaNote`.
   * That guard exists because dismissing a trade level's box can leave a step
   * ARMED, so the click still travelling behind the dismissal would plant a
   * level nobody asked for. Nothing is armed here — a drawing's box arms
   * nothing — so eating that click would only cost the trader a legitimate one.
   */
  const commitDrawingNote = (overlayId: string, selectionKey: string, text: string) => {
    saveOverlayNote(overlayId, text)
    setNoteDismissedKey(selectionKey)
  }

  /**
   * Write a note into an overlay's extendData.
   *
   * Shared by a trade level and a plain drawing: both keep their sentence in the
   * same place, and `chartContext.ts` reads it from there for both (`reason(d)`
   * on a drawing's line, the `— reason: "…"` tail inside `ideaBlock` on a
   * level). The no-op guard matters — `notifyEdit` re-stages the AI context, so
   * rewriting the same text would read as a chart change and cost tokens.
   */
  const saveOverlayNote = (overlayId: string, text: string) => {
    const c = chartRef.current
    const o = c?.getOverlayById(overlayId)
    if (!c || !o) return
    const ed = (o.extendData ?? {}) as Record<string, unknown>
    if ((ed.note ?? '') === text.trim()) return
    c.overrideOverlay({ id: overlayId, extendData: { ...ed, note: text.trim() } })
    notifyEdit()
  }

  /**
   * Open the trade level under the pointer, if there is one. Same ±6 vertical
   * reach as the hover highlight, so what lights is what opens.
   *
   * Returns true when the click was consumed.
   */
  const openLevelUnder = (e: React.MouseEvent): boolean => {
    const c = chartRef.current
    const rect = chartWrapRef.current?.getBoundingClientRect()
    if (!c || !rect) return false
    const my = e.clientY - rect.top
    for (const id of drawIdsRef.current) {
      const o = c.getOverlayById(id)
      // Hidden by the walkthrough replay (not yet reached): snapped onto the last bar, not there.
      if (!o || o.visible === false || !isTradeMark(o.name)) continue
      const ed = (o.extendData ?? {}) as Record<string, unknown>
      if (ed.hidden === true) continue
      const p = asPixel(c.convertToPixel(o.points, { paneId: 'candle_pane', absolute: true }))
      if (!p || Math.abs(p.y - my) > 6) continue
      const role = roleOfSlot(typeof ed.slot === 'string' ? ed.slot : undefined)
      if (!role) continue
      if (typeof ed.ideaId === 'string' && ed.ideaId !== ideaEditRef.current?.ideaId) {
        enterIdeaEdit(ed.ideaId, false)
      }
      openIdeaNote(id, role, e.clientX - rect.left, my, typeof ed.note === 'string' ? ed.note : '')
      return true
    }
    return false
  }

  // ---- Pinning callouts onto the chart ---------------------------------------------------
  /** The trade level within ±6px of a wrapper-relative y — the hover highlight's reach. */
  const tradeLevelAt = (y: number): string | null => {
    const c = chartRef.current
    if (!c) return null
    for (const id of drawIdsRef.current) {
      const o = c.getOverlayById(id)
      if (!o || o.visible === false || !isTradeMark(o.name)) continue
      if ((o.extendData as Record<string, unknown> | undefined)?.hidden === true) continue
      const p = asPixel(c.convertToPixel(o.points, { paneId: 'candle_pane', absolute: true }))
      if (p && Math.abs(p.y - y) <= 6) return id
    }
    return null
  }

  /**
   * The drawing within reach of a pixel, by its geometry (pinHitTest) — not the engine's hit
   * test, which reaches ±2px and is blind while a dragged callout tail holds pointer capture.
   * Trade-idea levels are tradeLevelAt's; locked connectors are chrome.
   */
  const drawingAt = (px: { x: number; y: number }): string | null => {
    const c = chartRef.current
    if (!c) return null
    const finder = { paneId: 'candle_pane', absolute: true }
    const candidates: Array<{ id: string; name: string; pts: Array<{ x: number; y: number }> }> = []
    for (const id of drawIdsRef.current) {
      const o = c.getOverlayById(id)
      if (!o || o.visible === false || o.lock || isTradeMark(o.name)) continue
      if ((o.extendData as Record<string, unknown> | undefined)?.connector === true) continue
      const raw = c.convertToPixel(o.points ?? [], finder)
      const pts = (Array.isArray(raw) ? raw : [raw]).flatMap((p) =>
        p && typeof p.x === 'number' && typeof p.y === 'number' ? [{ x: p.x, y: p.y }] : [],
      )
      candidates.push({ id, name: o.name, pts })
    }
    return nearestDrawing(candidates, px)
  }

  /**
   * The chart point under a pixel, its time taken from the nearest REAL bar. Past the last
   * candle (the empty room before the price axis) klinecharts has no timestamp at all — and a
   * timestamp of 0 resolves to the first bar of the history, which is where a note pinned there
   * used to land. `bar` is the candle actually under the pointer, if there is one.
   */
  const chartPointAt = (px: { x: number; y: number }) => {
    const c = chartRef.current
    if (!c) return null
    const pt = asPoint(c.convertFromPixel([px], { paneId: 'candle_pane', absolute: true }))
    if (!pt || pt.value == null) return null
    const list = c.getDataList()
    const bar = pt.dataIndex != null ? list[pt.dataIndex] : undefined
    const nearest = bar ?? (list.length > 0 ? list[Math.min(Math.max(pt.dataIndex ?? list.length - 1, 0), list.length - 1)] : undefined)
    const timestamp = nearest?.timestamp ?? pt.timestamp
    if (timestamp == null) return null
    return { bar, at: { timestamp, value: pt.value } }
  }

  /** The bar's O/H/L/C nearest a price — the rail's magnet, for a pin on a candle. */
  const snapToBar = (bar: { open: number; high: number; low: number; close: number }, value: number): number =>
    [bar.open, bar.high, bar.low, bar.close].reduce((best, v) => (Math.abs(v - value) < Math.abs(best - value) ? v : best))

  /**
   * What a pin takes at a pixel: the drawing the engine picked, else a trade level or a drawing
   * within reach, else the candle under the pointer when the price sits on it, else the spot
   * itself. Shared by the Callout tool and a callout tail being dragged.
   */
  const pinTargetAt = (px: { x: number; y: number }, pickedId: string | null): PinTarget | null => {
    const c = chartRef.current
    const here = chartPointAt(px)
    if (!c || !here) return null
    const finder = { paneId: 'candle_pane', absolute: true }
    const { bar, at } = here

    const id = (pickedId && drawIdsRef.current.has(pickedId) ? pickedId : null) ?? tradeLevelAt(px.y) ?? drawingAt(px)
    const o = id ? c.getOverlayById(id) : null
    if (o) {
      const d: SavedDrawing = {
        name: o.name,
        points: (o.points ?? []).map((p) => ({ timestamp: p.timestamp, value: p.value })),
        ...(o.extendData ? { extendData: o.extendData as Record<string, unknown> } : {}),
      }
      const edx = d.extendData ?? {}
      if (isTradeMark(o.name) && typeof edx.ideaId === 'string' && typeof edx.slot === 'string') {
        // A level spans the chart: the pin goes where it was clicked, on the level's price.
        const level = { timestamp: at.timestamp, value: d.points[0]?.value ?? at.value }
        return {
          pin: { kind: 'ideaLevel', ideaId: edx.ideaId, slot: edx.slot, at: level },
          label: tradeLevelTitle(d) ?? 'Trade idea',
        }
      }
      if (typeof edx.ctxId === 'string') return { pin: { kind: 'drawing', ctxId: edx.ctxId, at }, label: drawingTitle(d) }
    }
    if (bar) {
      const hi = asPixel(c.convertToPixel({ value: bar.high }, finder))?.y
      const lo = asPixel(c.convertToPixel({ value: bar.low }, finder))?.y
      if (hi != null && lo != null && px.y >= hi - 4 && px.y <= lo + 4) {
        const onBar = magnetRef.current ? { ...at, value: snapToBar(bar, at.value) } : at
        return {
          pin: { kind: 'candle', at: onBar, ohlc: { open: bar.open, high: bar.high, low: bar.low, close: bar.close } },
          label: `${fmtFriendly(bar.timestamp)} candle`,
        }
      }
    }
    return { pin: { kind: 'point', at }, label: '' }
  }

  /**
   * Where a new note's bubble goes: up and to the right of its pin, like a callout always did —
   * or up and to the LEFT when the right side has no room before the price axis. Decided once:
   * afterwards the note stays where it is on the chart and pans with it.
   */
  const bubbleFor = (at: NotePoint): { pos: NotePoint; left: boolean } => {
    const c = chartRef.current
    if (!c) return { pos: at, left: false }
    const finder = { paneId: 'candle_pane', absolute: true }
    const atPx = asPixel(c.convertToPixel({ timestamp: at.timestamp, value: at.value }, finder))
    // 0 means "not measured" (hidden, or jsdom), not "no room".
    const plotW = c.getDom('candle_pane', DomPosition.Main)?.clientWidth || chartWrapRef.current?.clientWidth || Infinity
    const left = !!atPx && atPx.x + 10 + NOTE_BUBBLE_W > plotW
    const above = atPx ? chartPointAt({ x: atPx.x + (left ? -10 : 10), y: atPx.y - 72 }) : null
    return { pos: above?.at ?? at, left }
  }

  /** An armed pin tool's click: the target, or null for none here. */
  const pinTargetForClick = (e: React.MouseEvent): PinTarget | null => {
    const c = chartRef.current
    const rect = chartWrapRef.current?.getBoundingClientRect()
    const picked = pinPickRef.current
    pinPickRef.current = null
    if (!c || !rect) return null
    // Only the price pane takes a pin — a sub-pane has no price of its own. A drawing the
    // engine handed over always does.
    const candleDom = paneDom(c, 'candle_pane')
    if (!picked && (!candleDom || !candleDom.contains(e.target as Node))) return null
    return pinTargetAt({ x: e.clientX - rect.left, y: e.clientY - rect.top }, picked)
  }

  /** The armed Callout's click: pin a callout there — its tail on the pin — and open it for typing. */
  const placeCallout = (e: React.MouseEvent) => {
    const target = pinTargetForClick(e)
    if (!target) return
    const { pos, left } = bubbleFor(target.pin.at)
    const id = `note${++noteIdRef.current}`
    setNotes((ns) => [
      ...ns,
      {
        id,
        kind: 'callout',
        tail: target.pin.at,
        pin: target.pin,
        pinLabel: target.label,
        ...(left ? { side: 'left' as const } : {}),
        pos,
        text: '',
        size: 14,
        bold: false,
        color: '', // unset: the text takes the theme's default (calloutText)
        bg: CALLOUT_BG,
      },
    ])
    setSelectedNoteId(id)
    setEditingNoteId(id)
    endDrawing()
  }

  /** While a pin tool is armed: say under the pointer what a click would pin to. */
  const onPinGuideMove = (e: React.PointerEvent) => {
    const tool = activeToolRef.current
    if (!isPinTool(tool)) {
      setPinGuide((v) => (v == null ? v : null))
      return
    }
    const c = chartRef.current
    const rect = chartWrapRef.current?.getBoundingClientRect()
    if (!c || !rect) return
    const candleDom = paneDom(c, 'candle_pane')
    if (!candleDom || !candleDom.contains(e.target as Node)) {
      setPinGuide(null)
      return
    }
    const px = { x: e.clientX - rect.left, y: e.clientY - rect.top }
    const t = pinTargetAt(px, null)
    setPinGuide(t ? { x: px.x, y: px.y, target: t.pin.kind === 'point' ? 'this spot' : t.label } : null)
  }

  // ---- Chart click routing -----------------------------------------------------------
  const onChartClick = (e: React.MouseEvent) => {
    // A touch tap already ran this routing via onTouchTapUp. klinecharts
    // usually suppresses the tap's compat click; when it doesn't (cancelled
    // taps, non-canvas children), swallow the duplicate.
    if (e.type === 'click' && nowMs() - lastTouchTapRef.current < 500) return
    // A note box was just dismissed by pressing the chart. That pointerdown
    // closed the box; its click must not also plant a level.
    if (swallowChartClickRef.current) {
      swallowChartClickRef.current = false
      return
    }
    // A walkthrough trade-arrow click just opened the order card (handleWtMarkerClick);
    // any other chart click dismisses it.
    if (orderMarkerClickRef.current) {
      orderMarkerClickRef.current = false
    } else if (wtOrderCard) {
      setWtOrderCard(null)
    }
    if (!chart) return
    // Pan/zoom stays live while click-to-place tools are armed, and a pan-drag
    // fires a trailing click on release — if the pointer travelled since the
    // press, this "click" was a pan: place no marker, note, or selection dot.
    const press = pressPtRef.current
    pressPtRef.current = null
    if (press && Math.hypot(e.clientX - press.x, e.clientY - press.y) > tapSlop(press.touch)) {
      pinPickRef.current = null
      return
    }
    // An armed pin tool (the Callout) owns the click, whatever is under it: a drawing
    // it pins to (never opens), a trade-idea level, a candle, or an empty spot.
    if (isPinTool(activeToolRef.current)) {
      drawingClickRef.current = false
      placeCallout(e)
      return
    }
    // The engine already handed this click to a drawing (its onClick/onSelected
    // ran first and set the flag). One click is one act: a press that landed on
    // a trade level opens THAT level, and must not also plant a new one on top
    // of it — the defect behind "clicking after Save made another take profit".
    //
    // Unless a step is armed, in which case placing wins and the drawing keeps
    // nothing: see the matching guard in selectFromEvent.
    if (armedStepRef.current) {
      drawingClickRef.current = false
    } else if (drawingClickRef.current) {
      drawingClickRef.current = false
      return
    }
    if (placeIdeaMark(e)) return
    // A text note: a click places it and opens it for edit (callouts pin, above).
    if (activeToolRef.current?.note === 'text') {
      const candleDom = paneDom(chart, 'candle_pane')
      if (!candleDom || !candleDom.contains(e.target as Node)) return
      createTextNote(e.clientX, e.clientY)
      return
    }
    if (activeTool !== 'cursor') return
    // A trade level is opened by clicking it. Done here rather than left to the
    // engine's own hit test, which reaches only ±2px: the hover highlight
    // lights a level from ±6, and a level that lights up but refuses to open
    // is worse than one that never lit at all.
    if (!armedStepRef.current && openLevelUnder(e)) return
    if (selectedNoteId) setSelectedNoteId(null) // clicking empty chart deselects a note
    // A click that's really the tail of a handle/box drag must NOT read as click-away.
    if (nowMs() - lastManipEndRef.current < 250) return
    // Clicking away lets go of the selected drawing: its toolbar and comment box close
    // (the box has already saved on this press). Clicking the drawing again brings both
    // back. Trade-idea levels keep their own flow.
    if (selected && !isTradeMark(selected.name)) setSelected(null)
    // TV-style click selection: drop a marker dot at the clicked point (on a candle
    // or snapped onto a main-pane indicator line).
    const subInst = indicators.find((i) => i.paneId !== 'candle_pane' && paneDom(chart, i.paneId)?.contains(e.target as Node))
    const candleDom = paneDom(chart, 'candle_pane')
    const onCandle = candleDom && candleDom.contains(e.target as Node)
    if (!subInst && !onCandle) return
    const paneId = subInst ? subInst.paneId : 'candle_pane'
    const rect = chartWrapRef.current?.getBoundingClientRect()
    const px = { x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) }
    const pt = asPoint(chart.convertFromPixel([px], { paneId, absolute: true }))
    if (!pt || pt.dataIndex == null || pt.value == null) return
    let value = pt.value
    if (onCandle) {
      // snap onto a main-pane indicator line if the click is near one
      for (const inst of indicators) {
        if (inst.paneId !== 'candle_pane') continue
        const ind = getIndicator(chart, 'candle_pane', inst.engineName)
        const row = (ind?.result as Array<Record<string, number | undefined>> | undefined)?.[pt.dataIndex]
        if (!row || !ind) continue
        const hit = (ind.figures ?? [])
          .map((f) => row[f.key])
          .find((v) => {
            if (v == null) return false
            const c = asPixel(chart.convertToPixel({ value: v }, { paneId: 'candle_pane', absolute: true }))
            return c != null && Math.abs(c.y - px.y) <= 6
          })
        if (hit != null) {
          value = hit
          break
        }
      }
    }
    const bar = chart.getDataList()[pt.dataIndex]
    clearChartSelection()
    selDotRef.current = asOverlayId(
      chart.createOverlay(
        {
          name: 'wbSelectionDot',
          points: [{ timestamp: bar?.timestamp ?? pt.timestamp, dataIndex: pt.dataIndex, value }],
          lock: true,
        },
        paneId,
      ),
    )
  }

  const onChartDoubleClick = (e: React.MouseEvent) => {
    if (activeTool !== 'cursor' || !chart) return
    clearChartSelection()
    // Double-click opens settings. On an indicator → THAT indicator's settings; a
    // sub-pane maps wholesale to its instance; on the candle pane we hit-test the
    // main-pane indicator lines near the cursor. Empty candle area → chart settings.
    const sub = indicators.find((i) => i.paneId !== 'candle_pane' && paneDom(chart, i.paneId)?.contains(e.target as Node))
    if (sub) {
      setSettingsId(sub.id)
      return
    }
    const mainDom = paneDom(chart, 'candle_pane')
    if (!mainDom || !mainDom.contains(e.target as Node)) return
    const rect = chartWrapRef.current?.getBoundingClientRect()
    const px = { x: e.clientX - (rect?.left ?? 0), y: e.clientY - (rect?.top ?? 0) }
    const di = asPoint(chart.convertFromPixel([px], { paneId: 'candle_pane', absolute: true }))?.dataIndex
    const hit =
      di != null &&
      indicators.find((inst) => {
        if (inst.paneId !== 'candle_pane') return false
        const ind = getIndicator(chart, 'candle_pane', inst.engineName)
        const row = (ind?.result as Array<Record<string, number | undefined>> | undefined)?.[di]
        if (!row || !ind) return false
        return (ind.figures ?? []).some((f) => {
          const v = row[f.key]
          if (v == null) return false
          const c = asPixel(chart.convertToPixel({ value: v }, { paneId: 'candle_pane', absolute: true }))
          return c != null && Math.abs(c.y - px.y) <= 6
        })
      })
    if (hit) setSettingsId(hit.id)
    else setSettingsOpen(true)
  }

  // ---- Drawing edit handlers ----------------------------------------------------------
  const handleDelete = (id: string) => {
    if (!chart || !id) return
    // A trade-idea level stands alone — deleting a take profit must not take
    // the entry with it. (Legacy entry→exit setups still cascade, below.)
    const t = chart.getOverlayById(id)
    if (t && isTradeMark(t.name)) {
      chart.removeOverlay(id)
      drawIdsRef.current.delete(id)
      setSelected(null)
      notifyEdit()
      return
    }
    // Deleting one leg of an entry→exit setup deletes the WHOLE setup: the
    // sibling marker and dashed connector would otherwise be left floating.
    const removeIds = new Set([id])
    const target = chart.getOverlayById(id)
    const targetEd = (target?.extendData ?? {}) as Record<string, unknown>
    if (target && SETUP_MARKERS.includes(target.name) && typeof targetEd.setupId === 'string') {
      const { entry, exit, connector } = setupOverlays(targetEd.setupId)
      for (const o of [entry, exit, connector]) if (o) removeIds.add(o.id)
    }
    for (const rid of removeIds) {
      chart.removeOverlay(rid)
      drawIdsRef.current.delete(rid)
    }
    setSelected(null)
    notifyEdit()
  }

  // ---- Keyboard: Delete/Escape + live Cmd magnet invert -----------------------------
  useEffect(() => {
    if (!chart) return
    const setMode = (id: string | null, cmd: boolean) => {
      if (id) chart.overrideOverlay({ id, mode: (magnetRef.current !== cmd ? 'strong_magnet' : 'normal') as OverlayCreate['mode'] })
    }
    const down = (e: KeyboardEvent) => {
      if (e.key === 'Meta' || e.key === 'Control') {
        cmdHeldRef.current = true
        setCmdHeld(true)
        setMode(progressIdRef.current, true)
        if (pressedIsHandleRef.current) setMode(pressedIdRef.current, true)
      }
      // A modal question owns the keyboard while it is up. Without this, the
      // Escape that dismisses the dialog also runs the chart's own Escape chain
      // underneath it — so backing out of "are you sure?" would ALSO throw you
      // out of the idea you were editing.
      if (confirmAskRef.current) return
      const t = e.target as HTMLElement | null
      const typing = /^(INPUT|TEXTAREA)$/.test(t?.tagName ?? '') || t?.isContentEditable
      // Esc aborts an in-progress drawing and returns to the cursor.
      // Escape backs out one layer at a time: stop placing, then leave the
      // idea. Never destructive — whatever is already marked stays.
      // An open note box takes Escape first — it is the innermost layer. (The
      // box's own textarea handles the key while focused; this catches the case
      // where focus has moved on.)
      if (e.key === 'Escape' && ideaNoteRef.current) {
        e.preventDefault()
        commitIdeaNote(ideaNoteRef.current.initial)
        return
      }
      // Click-to-place note tools (Callout, Text) put down without placing.
      if (e.key === 'Escape' && activeToolRef.current?.note) {
        e.preventDefault()
        endDrawing()
        return
      }
      if (e.key === 'Escape' && armedStepRef.current) {
        e.preventDefault()
        setArmedStep(null)
        armedStepRef.current = null
        setIdeaGuide(null)
        return
      }
      if (e.key === 'Escape' && ideaEditRef.current) {
        e.preventDefault()
        exitIdeaEdit()
        return
      }
      if (e.key === 'Escape' && (activeToolRef.current?.overlay || progressIdRef.current || previewIdRef.current)) {
        if (progressIdRef.current) chart.removeOverlay(progressIdRef.current)
        if (previewIdRef.current) chart.removeOverlay(previewIdRef.current)
        previewIdRef.current = null
        endDrawing()
      }
      if (e.key === 'Escape') {
        clearChartSelection()
        if (selectedRef.current && !isTradeMark(selectedRef.current.name)) setSelected(null)
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedRef.current && !typing) {
        e.preventDefault()
        handleDelete(selectedRef.current.id)
      }
    }
    const up = (e: KeyboardEvent) => {
      if (e.key === 'Meta' || e.key === 'Control') {
        cmdHeldRef.current = false
        setCmdHeld(false)
        setMode(progressIdRef.current, false)
        if (pressedIsHandleRef.current) setMode(pressedIdRef.current, false)
      }
    }
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart])

  // Icon shows the *effective* magnet state — Cmd is "in play" while drawing/editing.
  const cmdRelevant = activeTool !== 'cursor' || selected != null || editing
  const effectiveMagnet = cmdRelevant ? magnet !== cmdHeld : magnet

  // Remember the selected drawing's current look, keyed by its type, so the NEXT
  // new instance of that type starts with it.
  const recordDrawStyle = (id: string) => {
    const o = chart?.getOverlayById(id)
    if (!o) return
    const clean = (obj: Record<string, unknown>) =>
      Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined))
    const name = o.name
    let mem: (typeof drawStyleRef.current)[string]
    if (name === 'rectangle') {
      const p = o.styles?.polygon ?? {}
      mem = { polygon: clean({ color: p.color, borderColor: p.borderColor, borderSize: p.borderSize, borderStyle: p.borderStyle }) }
    } else if (name === 'fibRetracement') {
      const ed = (o.extendData ?? {}) as Record<string, unknown>
      mem = { extendData: clean({ levels: ed.levels, fillColor: ed.fillColor, lineSize: ed.lineSize, labelMode: ed.labelMode }) }
    } else {
      const l = o.styles?.line ?? {}
      mem = { line: clean({ color: l.color, size: l.size, style: l.style }) }
    }
    drawStyleRef.current = { ...drawStyleRef.current, [name]: mem }
  }

  const handleStyleMutated = () => {
    if (selected) recordDrawStyle(selected.id)
    notifyEdit()
  }

  // Extend a selected trend line. klinecharts has no "extend" style, so the line
  // swaps overlay type while keeping its points + styles: segment (none) ↔ rayLine
  // (one side) ↔ straightLine (both). Fib extends via its extendData flag.
  const handleExtend = (mode: string) => {
    if (!chart || !selected) return
    if (selected.name === 'fibRetracement') {
      const fib = chart.getOverlayById(selected.id)
      const ed = { ...((fib?.extendData ?? {}) as Record<string, unknown>), extend: mode }
      chart.overrideOverlay({ id: selected.id, extendData: ed })
      setSelected({ ...selected, extend: mode })
      notifyEdit()
      return
    }
    const ov = chart.getOverlayById(selected.id)
    if (!ov) return
    const pts = [...ov.points].sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0))
    let name = 'segment'
    if (mode === 'both') name = 'straightLine'
    else if (mode === 'left') {
      name = 'rayLine'
      pts.reverse()
    } else if (mode === 'right') name = 'rayLine'
    const styles = ov.styles
    // CARRY THE extendData ACROSS. The swap destroys and recreates the overlay,
    // so anything living in extendData dies with it unless it is handed over —
    // and two things there matter:
    //  · `note`, the trader's sentence about this line. Losing it because they
    //    extended the line they had just explained is indefensible.
    //  · `ctxId`, the line's identity in the AI's chart snapshot. Without it
    //    `trackCreate` stamps a fresh one, and extending a line reads to the
    //    model as one drawing removed and a different one added, rather than the
    //    same line changed.
    // `cloneOverlay` below already does the equivalent for the same reason.
    const extendData = ov.extendData
      ? (JSON.parse(JSON.stringify(ov.extendData)) as Record<string, unknown>)
      : undefined
    chart.removeOverlay(selected.id)
    drawIdsRef.current.delete(selected.id)
    const newId = asOverlayId(
      chart.createOverlay({ name, points: pts, styles, extendData, onDrawEnd: () => true, ...overlayEvents }),
    )
    if (newId) trackCreate(newId)
    if (!newId) return
    setSelected({
      id: newId,
      key: selected.key, // keep the same key so the pill survives the swap
      name,
      extend: mode,
      x: selected.x,
      y: selected.y,
      paneW: chartWrapRef.current?.clientWidth ?? 800,
    })
  }

  const handleFibConfig = (patch: FibConfigPatch) => {
    if (!chart || !selected) return
    const fib = chart.getOverlayById(selected.id)
    const ed = { ...((fib?.extendData ?? {}) as Record<string, unknown>), ...patch }
    chart.overrideOverlay({ id: selected.id, extendData: ed })
    setSelected({ ...selected, ...patch })
    recordDrawStyle(selected.id)
    notifyEdit()
  }

  // Clone the selected drawing: drop a copy nudged a clear diagonal step away.
  const cloneOverlay = (id: string) => {
    if (!chart || !id) return
    const o = chart.getOverlayById(id)
    if (!o?.points?.length) return
    const OFF = 32 // px diagonal nudge
    const finder = { paneId: 'candle_pane', absolute: true }
    const px = chart.convertToPixel(o.points, finder)
    const movedPx = (Array.isArray(px) ? px : [px]).map((c) => ({ x: (c.x ?? 0) + OFF, y: (c.y ?? 0) + OFF }))
    const back = chart.convertFromPixel(movedPx, finder)
    const points = (Array.isArray(back) ? back : [back]).map((p) => ({ timestamp: p.timestamp, dataIndex: p.dataIndex, value: p.value }))
    const extendData = o.extendData ? (JSON.parse(JSON.stringify(o.extendData)) as Record<string, unknown>) : undefined
    // A clone is a standalone marker: keeping setupId would tie it into the
    // original setup's connector sync and cascade delete.
    if (extendData && SETUP_MARKERS.includes(o.name)) delete extendData.setupId
    // …and it is a drawing of its own: trackCreate mints its ctxId. Sharing the
    // original's would make the two one item to the AI, and a callout pinned to
    // either would not know which it meant.
    if (extendData) delete extendData.ctxId
    const newId = asOverlayId(
      chart.createOverlay({
        name: o.name,
        points,
        styles: JSON.parse(JSON.stringify(o.styles ?? {})) as OverlayCreate['styles'],
        extendData,
        onDrawEnd: () => true,
        ...overlayEvents,
      }),
    )
    if (newId) trackCreate(newId)
  }

  // ---- Notes ---------------------------------------------------------------------------
  // Create an on-chart text note at a click (callouts are pinned instead: placeCallout).
  const createTextNote = (clientX: number, clientY: number) => {
    if (!chart) return
    const rect = chartWrapRef.current?.getBoundingClientRect()
    const here = chartPointAt({ x: clientX - (rect?.left ?? 0), y: clientY - (rect?.top ?? 0) })
    if (!here) return
    const id = `note${++noteIdRef.current}`
    const pos = here.at
    setNotes((ns) => [...ns, { id, kind: 'text', pos, text: '', size: 14, bold: false, color: '', bg: '' }])
    setSelectedNoteId(id)
    setEditingNoteId(id)
    endDrawing()
  }

  // ---- Composer ---------------------------------------------------------------------------
  // Lock the composer: write the note into the marker's extendData.
  const submitComposer = (_legKind: string, reason: string) => {
    if (!composer || !chart) return
    if (composer.editId) {
      const o = chart.getOverlayById(composer.editId)
      chart.overrideOverlay({ id: composer.editId, extendData: { ...((o?.extendData ?? {}) as Record<string, unknown>), note: reason } })
      setComposer(null)
      notifyEdit() // marker note lives in overlay extendData
      setSelected(null)
      endDrawing()
    }
  }

  const cancelComposer = () => {
    setComposer(null)
    endDrawing()
  }

  /**
   * Re-open a marker's note to edit it (hover card / floating toolbar Edit).
   *
   * The two tools part company here: a trade-idea level opens its own note box,
   * a legacy buy/sell marker opens the legacy composer. Re-opening never sets a
   * baton, so closing arms nothing — revisiting a level is not placing one.
   */
  const editLeg = (id: string) => {
    if (!chart || composer || ideaNote) return
    const o = chart.getOverlayById(id)
    if (!o?.extendData) return
    const ed = o.extendData as { note?: string; leg?: string; label?: string; precision?: number; slot?: string }
    const pt = o.points?.[0]
    const c = asPixel(chart.convertToPixel(o.points, { paneId: 'candle_pane', absolute: true }))

    if (isTradeMark(o.name)) {
      const role = roleOfSlot(ed.slot)
      if (role) openIdeaNote(id, role, c?.x ?? 0, c?.y ?? 0, ed.note ?? '')
      return
    }

    setSelected(null)
    setHoverNote(null)
    setComposer({
      relX: c?.x ?? 0,
      relY: c?.y ?? 0,
      price: typeof pt?.value === 'number' ? pt.value.toFixed(ed.precision ?? 2) : String(pt?.value ?? ''),
      timeLabel: dateLabel(pt?.timestamp),
      prec: ed.precision ?? 2,
      defaultLeg: ed.leg,
      initialValue: ed.note ?? '',
      editId: id,
    })
  }

  // ---- Hover note card ----------------------------------------------------------------------
  const hoverCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hoverCardRef = useRef<HTMLDivElement | null>(null)
  const cancelHoverClose = () => {
    if (hoverCloseRef.current) {
      clearTimeout(hoverCloseRef.current)
      hoverCloseRef.current = null
    }
  }
  const scheduleHoverClose = () => {
    cancelHoverClose()
    hoverCloseRef.current = setTimeout(() => {
      hoverCloseRef.current = null
      setHoverNote(null)
    }, 160)
  }

  const onHoverNoteMove = (e: React.PointerEvent) => {
    // Pointer over the card itself (moving toward Edit) — the card's own
    // enter/leave owns closing; keep it open.
    if (hoverCardRef.current?.contains(e.target as Node)) {
      cancelHoverClose()
      return
    }
    if (!chart || activeTool !== 'cursor' || composer || selected) {
      if (hoverNote) {
        cancelHoverClose()
        setHoverNote(null)
      }
      stampLineFocus(null)
      return
    }
    const rect = chartWrapRef.current?.getBoundingClientRect()
    if (!rect) return
    const mx = e.clientX - rect.left
    const my = e.clientY - rect.top
    let found: HoverNote | null = null
    /** The idea under the pointer, to light while the cursor is on it. */
    let lit: string | null = null
    drawIdsRef.current.forEach((id) => {
      if (found && lit) return
      const o = chart.getOverlayById(id)
      // Hidden by the walkthrough replay (not yet reached): snapped onto the last bar, not there.
      if (!o || o.visible === false) return
      const ed = o.extendData as
        | { note?: string; leg?: string; label?: string; precision?: number; ideaId?: string; hidden?: boolean }
        | undefined
      if (!ed) return
      const p = asPixel(chart.convertToPixel(o.points, { paneId: 'candle_pane', absolute: true }))
      if (!p) return

      // A TRADE-IDEA LEVEL gets no card. Hovering it lights the whole idea on
      // the canvas instead — the plan's shape, drawn where the plan is. It is
      // hit by vertical proximity anywhere across the pane, because it draws a
      // full-width price line. A hidden level draws nothing, so nothing is
      // under the pointer to light.
      if (isTradeMark(o.name)) {
        if (ed.hidden === true) return
        if (Math.abs(p.y - my) <= 6 && typeof ed.ideaId === 'string') lit = ed.ideaId
        return
      }

      // LEGACY buy/sell markers keep their hover card, unchanged.
      if (!LEG_MARKERS.includes(o.name)) return // note optional — still show the card
      const near =
        o.name === 'levelLine'
          ? Math.abs(p.y - my) <= 6
          : Math.abs(p.x - mx) <= 14 && Math.abs(p.y - my) <= 22
      if (near && !found) {
        const value = o.points?.[0]?.value
        found = {
          id: o.id,
          x: p.x,
          y: p.y,
          leg: ed.leg,
          label: ed.label ?? '',
          note: ed.note ?? '',
          price: typeof value === 'number' ? value.toFixed(ed.precision ?? 2) : '',
        }
      }
    })
    stampLineFocus(lit)
    if (found) {
      cancelHoverClose()
      const f: HoverNote = found
      setHoverNote((prev) => (prev && prev.id === f.id && prev.note === f.note && Math.abs(prev.y - f.y) < 2 ? prev : f))
    } else if (hoverNote) {
      scheduleHoverClose() // leave a window to move the cursor onto the card
    }
  }

  // ---- Restore (applies a pending slice once its symbol's candles land) ---------------------
  // Runs from the chart-status event callback, not an effect: indicators and
  // drawings are engine mutations plus setState, which effects here can't do
  // synchronously (react-hooks/set-state-in-effect). Serves both the mount
  // restore and every symbol switch back to a previously-seen symbol.
  const applyPendingRestore = (s: WorkbenchChartStatus) => {
    const pending = pendingRestoreRef.current
    const c = chartRef.current
    if (!pending || !c) {
      pendingRestoreRef.current = null
      setRestoreDone(true)
      return
    }
    // A terminal status for another symbol's load (stale or superseded) must
    // not consume the pending slice — keep waiting for the right one.
    if (s.symbol && s.symbol !== pending.symbol) return
    if (pending.symbol !== symbolRef.current) return
    pendingRestoreRef.current = null
    const saved = pending.slice

    const restored: IndicatorInstance[] = []
    for (const entry of saved.indicators) {
      const option = indicatorByName(entry.name)
      if (!option) continue
      // Mint in saved array order — deterministic (base, #2, #3, …), matching
      // the per-base ordinal keys the AI context uses.
      const engineName = mintEngineName(option.name, restored.map((i) => i.engineName))
      if (engineName !== option.name) {
        const src = restored.find((i) => i.name === option.name)
        if (!src || !ensureIndicatorClone(c, src, engineName, option.name)) continue
      }
      const res = applyIndicator(c, option, entry.settings, engineName)
      if (res) {
        restored.push({ id: `i${++instCounterRef.current}`, name: option.name, engineName, paneId: res.paneId, bandIds: res.bandIds, settings: res.settings })
      }
    }
    if (restored.length > 0) setIndicators(restored)

    // Drawings + view need candles to anchor to — skip them on an
    // empty/errored window (indicators above still restore).
    if (!s.error && !s.empty) {
      let stamped = 0
      for (const d of saved.drawings) {
        const id = asOverlayId(
          c.createOverlay({
            name: d.name,
            points: d.points,
            ...(d.styles ? { styles: d.styles as OverlayCreate['styles'] } : {}),
            ...(d.extendData ? { extendData: d.extendData } : {}),
            ...(d.lock ? { lock: true } : {}),
            // Load-bearing: without onDrawEnd the engine enters interactive draw mode.
            onDrawEnd: () => true,
            // Locked connectors are chrome, not editable drawings (entry-flow precedent).
            ...(d.lock ? {} : overlayEvents),
          }),
        )
        if (id) {
          drawIdsRef.current.add(id)
          // Backfill ctxIds on legacy saves so the AI-context diff has stable keys.
          if (!d.lock && typeof (d.extendData ?? {}).ctxId !== 'string') {
            stampCtxId(id)
            stamped++
          }
        }
      }
      if (stamped > 0) markDirty() // persist the backfill (not a user edit)
      // Jump back to the last location: restore zoom, then scroll so the saved
      // last-visible candle sits at the right edge. Beyond the initially loaded
      // bars this clamps to the left edge; pagination fills in as the user pans.
      if (saved.view.barSpace) c.setBarSpace(saved.view.barSpace)
      if (saved.view.toTs != null) c.scrollToTimestamp(saved.view.toTs * 1000)
    }
    publishDrawCount(drawIdsRef.current.size)
    setDrawVersion((v) => v + 1)
    setIdeas(readIdeas())
    // Carry on numbering where the saved chart left off — otherwise the next
    // idea reuses #1's name and colour.
    ideaSeqRef.current = saved.drawings.reduce(
      (max, d) => {
        const seq = (d.extendData as { ideaSeq?: unknown } | undefined)?.ideaSeq
        return d.name === TRADE_MARK && typeof seq === 'number' && seq >= max ? seq + 1 : max
      },
      0,
    )

    if (pending.isMount) setRestoreDone(true)
    scheduleContextRefresh() // rebuild against the freshly swapped diff base
  }

  const handleChartStatus = (s: WorkbenchChartStatus) => {
    setStatus(s)
    // Terminal load status while a restore is pending → apply it. On an
    // empty/errored window we still restore indicators (they need no data).
    if (pendingRestoreRef.current && !s.loading) applyPendingRestore(s)
  }

  /**
   * Put the live canvas down so another slice can take its place.
   *
   * Shared by the symbol switch and by an imported setup. It does NOT snapshot
   * what is going away — whether the outgoing state is worth keeping is the
   * caller's question, and the two callers answer it differently: a symbol
   * switch files the old slice away, an import throws it out.
   */
  const tearDownCanvasForSwap = () => {
    setComposer(null)
    // Ideas are per-symbol (they live in the symbol's drawing slice), so the
    // edit session and the numbering both start over on the new market. Teardown,
    // not a save: the canvas is being swapped out from under the idea.
    resetIdeaEdit()
    setIdeaExpanded(new Set())
    ideaSeqRef.current = 0
    clearUserDrawings()
    clearChartSelection()
    setSelected(null)
    setHoverNote(null)
    setNotes([])
    setSelectedNoteId(null)
    setEditingNoteId(null)
    setWtOrderCard(null)
    endDrawing()
    setDrawCount(0)
  }

  // ---- Symbol change: each market keeps its own chart environment ----------------------------
  // Snapshot the outgoing symbol's state, wipe the canvas, then either queue
  // the incoming symbol's saved slice for restore (applied once its candles
  // land) or — for a never-seen symbol — inherit the current timeframe /
  // chart type / indicators / volume as its starting point.
  const handleSymbolChange = (next: string) => {
    if (next === symbol || !symbol) return
    // Unless the outgoing symbol's own restore never applied (the user
    // out-raced it — the canvas is still blank and its cached slice is the
    // truth), capture its live state.
    if (!pendingRestoreRef.current) {
      const out = snapshotActiveSlice()
      if (out) bySymbolRef.current[symbol] = out
    }
    tearDownCanvasForSwap()

    activeSinceRef.current = nowMs()
    const incoming = bySymbolRef.current[next]
    if (incoming) {
      // Seen before: replace the live environment with the saved slice.
      // Indicators come off the engine now (blank chart while loading) and
      // are recreated by the pending restore once candles land.
      removeAllIndicators()
      setTimeframe(incoming.timeframe)
      setChartType(incoming.chartType)
      setVolume(incoming.volume)
      setNotes(incoming.notes)
      noteIdRef.current = incoming.noteCounter
      aiPushRef.current = {
        sentAt: incoming.aiPush?.sentAt ?? null,
        sentItems: normalizeSentItems(incoming.aiPush?.sentItems ?? null),
        sentViews: incoming.aiPush?.sentViews ?? null,
      }
      pendingRestoreRef.current = { symbol: next, slice: incoming, isMount: false }
    } else {
      // Never seen: inherit timeframe/chartType/indicators/volume as-is
      // (blank drawings/notes done above, viewport defaults to latest bar).
      // Fresh AI base — the fingerprint effect rebuilds, and the inherited
      // indicators/notes read as unsent for this symbol. Intended.
      aiPushRef.current = { sentAt: null, sentItems: null, sentViews: null }
      pendingRestoreRef.current = null
      // No restore will land to publish for us, and a market nobody has drawn on really has
      // nothing to walk through — so this 0 IS the truth, unlike the wipe above.
      chartActivity.setDrawCount(bid, 0)
    }
    setSymbol(next)
  }

  // ---- Show State (dev aid): the exact payload the autosave persists ---------------------------
  const handleShowState = () => {
    setStateSnapshot(composeCurrentState())
    setShowState(true)
  }

  // ---- Carrying a chart setup between strategies ----------------------------------------------
  // Drawing the same ideas again on every new strategy is the tax this removes.
  // Export writes the blob the autosave would have written; import replaces the
  // slices the file names and leaves every other market alone.

  const handleExportSetup = () => {
    const state = composeCurrentState()
    if (!state) return
    downloadTransferFile(
      buildTransferFile(state, { bid, activeSymbol: state.activeSymbol }),
      transferFileName(state.activeSymbol, timeframe),
    )
  }

  /**
   * Is the chart in a state where an imported setup would actually land?
   *
   * The costly one is importing before the mount restore has applied: it would
   * consume that pending slice, lose its `isMount` flag, and leave
   * `restoreDone` false — autosave dead for the rest of the session, silently.
   */
  const importReadiness = (): { ok: boolean; why: string } => {
    if (cutoffActiveRef.current) return { ok: false, why: 'Hide the walkthrough before importing a chart setup.' }
    if (!restoreDone || pendingRestoreRef.current) {
      return { ok: false, why: 'The chart is still loading. Try again in a moment.' }
    }
    if (!chartRef.current || !symbol || symbols.length === 0) {
      return { ok: false, why: 'The chart is not ready yet.' }
    }
    return { ok: true, why: '' }
  }

  const IMPORT_REFUSALS: Record<TransferRejection, string> = {
    'not-json': 'That file is not JSON.',
    'not-workbench': 'That file is not an exported chart setup.',
    'unsupported-version': 'That file was written by a newer version of the app.',
    'unreadable-state': 'That file carries no chart setup this build can read.',
  }

  const refuseImport = (message: string) =>
    setConfirmAsk({
      open: true,
      title: 'Nothing imported',
      body: message,
      confirmLabel: 'OK',
      destructive: false,
      hideCancel: true,
      action: () => {},
    })

  const handleImportFile = async (file: File) => {
    const ready = importReadiness()
    if (!ready.ok) {
      refuseImport(ready.why)
      return
    }
    const read = readTransferFile(await file.text())
    if (!read.ok) {
      refuseImport(IMPORT_REFUSALS[read.reason])
      return
    }

    const listed = new Set(symbols.map((s) => s.symbol))
    const rows = describeSlices(read.file.state, bySymbolRef.current, listed)
    const usable = rows.filter((r) => !r.unavailable)
    if (usable.length === 0) {
      refuseImport(
        `That setup is for ${rows.map((r) => r.symbol).join(', ')}, which this account cannot chart.`,
      )
      return
    }

    // Typing the word is reserved for an import that would destroy work. On a
    // strategy with nothing drawn yet — the case this feature is for — a click
    // is enough.
    const overwrites = usable.filter(
      (r) => r.replaces && (bySymbolRef.current[r.symbol].drawings.length > 0
        || bySymbolRef.current[r.symbol].notes.length > 0),
    )
    const dropped = rows.filter((r) => r.unavailable).map((r) => r.symbol)
    // The cap is applied when the state is SAVED, and the imported slices are
    // the freshest, so the markets thrown out would be the ones already here.
    // Better said now than discovered later.
    const afterwards = new Set([...Object.keys(bySymbolRef.current), ...usable.map((r) => r.symbol)])
    const evicted = Math.max(0, afterwards.size - MAX_SYMBOL_SLICES)

    setConfirmAsk({
      open: true,
      title: 'Import this chart setup?',
      body: (
        <span className="block space-y-1">
          {usable.map((r) => (
            <span key={r.symbol} className="block">
              <strong>{r.symbol}</strong>
              {' — '}
              {countLabel(r.ideas, 'trade idea')}, {countLabel(r.drawings, 'drawing')},{' '}
              {countLabel(r.notes, 'note')}, {countLabel(r.indicators, 'indicator')}
              {r.replaces ? ', replacing what is on this market now' : ''}
            </span>
          ))}
          {dropped.length > 0 && (
            <span className="block text-muted-foreground">
              Skipping {dropped.join(', ')} — not available on this account.
            </span>
          )}
          {evicted > 0 && (
            <span className="block text-muted-foreground">
              This strategy would then hold {afterwards.size} markets. The {countLabel(evicted, 'market')}{' '}
              you looked at longest ago will be dropped when it next saves.
            </span>
          )}
          <span className="block text-muted-foreground">
            Chart colours and the backtest-trades toggle come across too. Nothing the other
            strategy&apos;s chat was shown does.
          </span>
        </span>
      ),
      confirmLabel: 'Import',
      destructive: overwrites.length > 0,
      ...(overwrites.length > 0 ? { typeToConfirm: 'replace' } : {}),
      action: () => applyImportedSetup(read.file),
    })
  }

  /**
   * Swap the imported slices in and put the chart on one of them.
   *
   * Reuses the symbol-switch machinery rather than recreating overlays here:
   * `pendingRestoreRef` plus `applyPendingRestore` is the one path that knows
   * how to rebuild indicators, drawings, the viewport and the idea numbering.
   */
  const applyImportedSetup = (file: WorkbenchTransferFile) => {
    if (!importReadiness().ok) return // re-checked: the dialog was open for a while
    const base = nowMs()
    const listed = new Set(symbols.map((s) => s.symbol))
    const merged = mergeImportedSlices(bySymbolRef.current, file.state.bySymbol, listed, base)
    const target = targetSymbolFor(file.state, merged.imported)
    const slice = target ? merged.bySymbol[target] : null
    if (!target || !slice) return

    // Same market, same timeframe and nothing in flight means no candle load,
    // so the restore has to run here — and it can only place drawings against
    // candles. With none on the chart it would drop them and say nothing, so
    // refuse now, before a single thing has been changed.
    const reloadComing = target !== symbol || slice.timeframe !== timeframe || status.loading
    if (!reloadComing && (chartRef.current?.getDataList().length ?? 0) === 0) {
      refuseImport('This market has no candles loaded yet. Wait for the chart, then import.')
      return
    }

    // The market on screen is only discarded when the file replaces it. An
    // import of XAUUSD while EURUSD is up must not drop the EURUSD drawings
    // made since the last save — the symbol switch files them away too.
    if (!file.state.bySymbol[symbol] && !pendingRestoreRef.current) {
      const out = snapshotActiveSlice()
      if (out) merged.bySymbol[symbol] = out
    }

    tearDownCanvasForSwap()
    bySymbolRef.current = merged.bySymbol
    setChartSettings(file.state.chartSettings)
    setShowWalkthrough(file.state.showWalkthrough ?? false)

    // The imported drawings have never been shown to THIS bot's chat. Keeping
    // the source bot's diff base would tell it they had been, and the AI would
    // never see them. Whatever was staged described the chart being replaced.
    aiPushRef.current = { sentAt: null, sentItems: null, sentViews: null }
    chartPush.clearStaged(bid)

    removeAllIndicators()
    setTimeframe(slice.timeframe)
    setChartType(slice.chartType)
    setVolume(slice.volume)
    setNotes(slice.notes)
    noteIdRef.current = slice.noteCounter
    // The same stamp the merged slices carry: a fresh Date.now() per serialize
    // would defeat the autosave's payload dedupe.
    activeSinceRef.current = base
    pendingRestoreRef.current = { symbol: target, slice, isMount: false }

    // A new symbol or timeframe reloads candles, and a load already in flight
    // will land one too; in every such case the restore rides that status, the
    // way a symbol switch does. Applying early instead would have the reload's
    // applyNewData throw away the viewport the restore had just set.
    setSymbol(target)
    if (!reloadComing) {
      // Nothing will reload, so no status will ever arrive to trigger the
      // restore. The flags are built here rather than read off `status`: a
      // stale error or empty left over from an earlier load would consume the
      // pending slice and silently drop every imported drawing.
      applyPendingRestore({ loading: false, error: null, empty: false, symbol: target })
    }
    // Drawings live in the engine, so an import can change everything the user
    // sees without touching a single field the autosave effect watches.
    markDirty()
  }

  // ---- Canvas suspension ---------------------------------------------------------------------
  /**
   * Put the canvas down: finish or drop anything half-open (a mid-draw overlay, an armed tool,
   * an open dialog), so nothing is left painted over whatever has covered the chart.
   */
  const suspendCanvasInteractions = () => {
    endDrawing()
    setActiveTool('cursor')
    clearChartSelection()
    setSelected(null)
    setComposer(null)
    // Teardown: a modal taking the canvas is not the user leaving their idea.
    resetIdeaEdit()
    setIdeaPanelOpen(false)
    setHoverNote(null)
    setCtxMenu(null)
    setDeleteMenu(null)
    setWtOrderCard(null)
    setSelectedNoteId(null)
    setEditingNoteId(null)
    setSettingsId(null)
    setIndicatorsOpen(false)
    setSymbolSearchOpen(false)
    setVolumeSettingsOpen(false)
    setSettingsOpen(false)
  }

  /**
   * The same teardown, asked for from OUTSIDE: a surface elsewhere in the app has
   * covered the chart (the mobile chat sheet, the mobile nav drawer), so the canvas
   * puts itself down instead of leaving an edit strip and a coloured rim painted
   * over someone else's modal. See bot/canvasSuspend.ts for why this is a signal
   * rather than another round of z-index.
   *
   * The subscription outlives every render, so it has to call the current teardown rather
   * than the closure it was registered with.
   */
  const suspendRef = useRef(suspendCanvasInteractions)
  useLayoutEffect(() => {
    suspendRef.current = suspendCanvasInteractions
  })
  useEffect(() => canvasSuspend.subscribe(() => suspendRef.current()), [])

  // ---- Walkthrough overlay ------------------------------------------------------------------
  // Not a mode any more: the bot's walkthrough is a layer of this canvas, shown or hidden from
  // the toolbar's Walkthrough toggle, and the drawing tools stay live the whole time. See
  // useWalkthroughOverlay.

  /** The active symbol's drawings as they are now — null mid-switch, when the canvas is blank. */
  const getLiveDrawings = () => (pendingRestoreRef.current ? null : (snapshotActiveSlice()?.drawings ?? null))

  const walkthroughOverlay = useWalkthroughOverlay({
    bid,
    // The chart STATE too, not just the restore: the drawings are read off the engine through
    // it, and it can land a render or two after the restore does.
    ready: restoreDone && !!chart,
    symbol,
    timeframe,
    getDrawings: getLiveDrawings,
    hasCode,
    codeId,
    requested: !!walkthrough,
    onRequestHandled: () => onWalkthroughChange?.(false),
    onShow: () => setShowWalkthrough(true),
  })
  const wtRun = walkthroughOverlay.run
  /** The run belongs to the symbol on the canvas — otherwise there is no walkthrough here. */
  const wtOnSymbol = !!wtRun && wtRun.symbol === symbol
  const wtPreparing = walkthroughOverlay.phase.kind === 'preparing'
  /** A run for this symbol is on hand, so the checkbox shows or hides it rather than runs one. */
  const wtHere = wtOnSymbol && walkthroughOverlay.phase.kind === 'idle'
  // Ticked while preparing too: the user asked for it, and it lands shown.
  const wtChecked = wtPreparing || (wtHere && showWalkthrough)

  const handleWalkthroughShownChange = (next: boolean) => {
    if (wtHere) {
      setWtOrderCard(null)
      setShowWalkthrough(next)
      return
    }
    // Nothing to show here yet: checking it runs one, and onShow ticks it once it lands.
    if (next) walkthroughOverlay.runWalkthrough()
  }

  /** The drawing being dragged or drawn keeps its visibility whatever the playhead does. */
  const isDrawingExempt = useCallback(
    (id: string) => pressedIdRef.current === id || progressIdRef.current === id,
    [],
  )
  /** A drawing the replay just hid cannot keep a toolbar pointing at it. */
  const handleDrawingsHidden = useCallback((ids: string[]) => {
    setSelected((cur) => (cur && ids.includes(cur.id) ? null : cur))
  }, [])
  /** A press or drag on the chart holds the replay's viewport writes. */
  const holdReplayViewport = useCallback(
    () => activePointersRef.current > 0 || pressedIdRef.current != null,
    [],
  )
  const handleCutoffChange = useCallback(
    (active: boolean) => {
      const was = cutoffActiveRef.current
      cutoffActiveRef.current = active
      // Drawing stays live during a replay, but the context is never rebuilt from a cut chart
      // (refreshChartContext bails) — so what was drawn meanwhile is staged once it lifts.
      if (was && !active) scheduleContextRefresh()
    },
    [scheduleContextRefresh],
  )

  // The replay moves the chart without an OnScroll: a revealed bar shifts every candle left.
  // The two pixel-anchored layers that do not follow the series on their own answer it here —
  // the trade card re-anchors while it is open, and a selection is dropped the way a pan drops
  // it (its floating toolbar would otherwise hang where the drawing used to be). Only while
  // something needs it, so an idle replay never re-renders the whole canvas per bar.
  const [, setCardNonce] = useState(0)
  useEffect(() => {
    if (!series || !wtOrderCard) return undefined
    return series.subscribe((e) => {
      if (e.type === 'data') setCardNonce((n) => n + 1)
    })
  }, [series, wtOrderCard])
  const hasSelection = !!selected
  useEffect(() => {
    if (!series || !hasSelection) return undefined
    return series.subscribe((e) => {
      if (e.type === 'data' && cutoffActiveRef.current) setSelected(null)
    })
  }, [series, hasSelection])

  /** Why a new walkthrough cannot start, if it cannot. */
  const wtRunBlockedReason = (() => {
    if (!restoreDone || !symbol) return 'Loading the chart…'
    if (!hasCode) {
      return 'Ask the AI to build your strategy first — the walkthrough replays your generated code.'
    }
    if (drawCount === 0) {
      return 'Draw your setup first — the walkthrough replays the period you drew on.'
    }
    return null
  })()
  /**
   * Why the Walkthrough toggle is unavailable, if it is. A run already on hand can always be
   * shown or hidden; only running a new one needs code and drawings.
   */
  const wtToggleDisabledReason = (() => {
    if (!restoreDone || !symbol) return 'Loading the chart…'
    if (wtPreparing) return 'Preparing the walkthrough…'
    return wtHere ? null : wtRunBlockedReason
  })()

  /**
   * The HTML note layers (callouts, text notes) are placed against the candles, by
   * timestamp and price. Until the candles they belong to are on the canvas — the mount
   * restore, a symbol switch, a timeframe reload — there is nothing to place them against:
   * klinecharts maps every point onto an empty chart, which piled them up in the bottom-right
   * corner over the "Loading candles…" canvas. Drawings already wait for that moment (the
   * restore applies them once the first candle load lands); the notes wait for it too.
   */
  const notesPlaceable =
    restoreDone && !status.loading && !status.error && !status.empty && (!status.symbol || status.symbol === symbol)

  /** Every interactive gesture on the canvas, collected into one object. */
  const canvasPointerHandlers = {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.pointerType === 'touch') setPointerOnChart(true)
      onDrawPointerDown(e)
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (e.pointerType !== 'touch') setPointerOnChart(true)
      onDrawPointerMove(e)
      onHoverNoteMove(e)
      onIdeaGuideMove(e)
      onPinGuideMove(e)
    },
    onPointerLeave: (e: React.PointerEvent) => {
      if (e.pointerType !== 'touch') {
        setPointerOnChart(false)
        // At the top of a narrow screen, what the pointer was over goes with it. (A refused
        // tap is kept: a finger has left by the time it can be read.)
        if (topReadout) setIdeaGuide(null)
      }
      if (hoverNote) scheduleHoverClose()
      setPinGuide(null)
    },
    onPointerUp: (e: React.PointerEvent) => {
      if (e.pointerType === 'touch') setPointerOnChart(false)
      onDrawPointerUp(e)
      onTouchTapUp(e)
    },
    onPointerCancel: () => {
      setPointerOnChart(false)
      activePointersRef.current = Math.max(0, activePointersRef.current - 1)
      pressPtRef.current = null // browser took the gesture (scroll/pinch) — no click follows
    },
    onClick: onChartClick,
    onDoubleClick: onChartDoubleClick,
    onContextMenu: (e: React.MouseEvent) => {
      // Right-click the chart canvas → the canvas menu. Not mid-draw.
      if (activeTool !== 'cursor' || (e.target as HTMLElement).tagName !== 'CANVAS') return
      e.preventDefault()
      setCtxMenu({ x: e.clientX, y: e.clientY, drawCount: drawIdsRef.current.size })
    },
  }

  // ---- Render -------------------------------------------------------------------------------------
  const settingsInstance = settingsId ? indicators.find((i) => i.id === settingsId) : undefined
  const settingsOption = settingsInstance ? indicatorByName(settingsInstance.name) : undefined
  const { w: wrapW, h: wrapH } = wrapSize
  const onbActive = restoreDone && typeof onbStep === 'number'
  const handleOnbNext = () => {
    setOnbStep((step) => {
      if (typeof step !== 'number') return step
      if (step >= ONB_STEPS.length - 1) {
        onboardingStorage.markDone()
        return 'done'
      }
      return step + 1
    })
  }
  const handleOnbSkip = () => {
    onboardingStorage.markDone()
    setOnbStep('done')
  }

  return (
    <div className="absolute inset-0 flex flex-col bg-background" data-bid={bid}>
      <WorkbenchTopToolbar
        chart={chart}
        captureRef={chartWrapRef}
        symbol={symbol}
        onOpenSymbolSearch={() => {
          setSymbolSeed('')
          setSymbolSearchOpen(true)
        }}
        timeframe={timeframe}
        onTimeframeChange={setTimeframe}
        chartType={chartType}
        onChartTypeChange={setChartType}
        onOpenIndicators={() => setIndicatorsOpen(true)}
        onOpenSettings={() => setSettingsOpen(true)}
        onShowState={handleShowState}
        walkthroughShown={wtChecked}
        onWalkthroughShownChange={handleWalkthroughShownChange}
        walkthroughExists={wtHere}
        walkthroughDisabledReason={wtToggleDisabledReason}
        ideaNav={<WorkbenchIdeaNav chart={chart} series={series} ideas={ideas} timeframe={timeframe} />}
      />
      {/* `relative` so the edit-mode rim at the bottom of this row can span the rail
          AND the chart. The rail has no absolutely-positioned descendants, so this
          takes nothing else off its old anchor. */}
      <div className="relative flex-1 min-h-0 flex">
        <WorkbenchDrawingRail
          activeTool={activeTool}
          onSelectTool={handleSelectTool}
          onOpenDeleteMenu={(pos) => setDeleteMenu({ ...pos, drawCount: drawIdsRef.current.size })}
          magnet={effectiveMagnet}
          onToggleMagnet={() => setMagnet((m) => !m)}
          tradeIdeasOpen={ideaPanelOpen}
          onToggleTradeIdeas={() => setIdeaPanelOpen((v) => !v)}
        />
        {/* The chart column: the chart box, then — when the bot has a walkthrough on this
            symbol — its band and trades pane UNDER it. As rows of this column they take their
            own height and the chart flexes down by exactly that, so its time axis stays fully
            visible and nothing covers a candle. */}
        <div className="relative flex-1 min-h-0 flex flex-col min-w-0">
        {/* Positioning parent for the chart and the chrome floated over it. The canvas keeps its
            own absolutely-positioned box, so every pixel measurement inside it (notes, pane
            controls, the order card) is unaffected. */}
        <div className="relative flex-1 min-h-0">
        <div
          ref={chartWrapRef}
          // touch-none: otherwise the browser claims a one-finger drag as its own
          // pan and sends pointercancel, killing drag-to-draw mid-stroke. The
          // engine pans and pinches from touch events, which this leaves alone.
          className="absolute inset-0 touch-none"
          data-testid="chart-area"
          {...canvasPointerHandlers}
        >
          <WorkbenchChart
            symbol={symbol}
            timeframe={timeframe}
            anchorTs={anchorTs}
            pricePrecision={pricePrecision}
            chartType={chartType}
            settings={chartSettings}
            onReady={onReady}
            onStatusChange={handleChartStatus}
          />
          {chart && symbol && (
            <WorkbenchStatusLine
              chart={chart}
              indicators={indicators}
              onToggleVisible={toggleIndicatorVisible}
              onOpenSettings={setSettingsId}
              onRemove={removeIndicatorById}
              volumePresent={volume.present}
              volumeVisible={volume.visible !== false}
              onToggleVolume={() => setVolume((v) => ({ ...v, visible: !(v.visible !== false) }))}
              onOpenVolumeSettings={() => setVolumeSettingsOpen(true)}
              onRemoveVolume={removeVolume}
            />
          )}
          {chart && (
            <WorkbenchPaneControls
              chart={chart}
              indicators={indicators}
              containerRef={chartWrapRef}
              nonce={vpNonce}
              onToggleVisible={toggleIndicatorVisible}
              onOpenSettings={setSettingsId}
              onRemove={removeIndicatorById}
            />
          )}
          {wtOrderCard &&
            chart &&
            wtOnSymbol &&
            showWalkthrough &&
            (() => {
              // Re-anchored every render — vpNonce bumps on pan/zoom, so the
              // card follows its arrow. Hidden once the arrow leaves the pane.
              const px = asPixel(
                chart.convertToPixel(
                  { timestamp: wtOrderCard.barTime * 1000, value: wtOrderCard.value },
                  { paneId: 'candle_pane', absolute: true },
                ),
              )
              const wrapWidth = chartWrapRef.current?.clientWidth ?? 0
              if (!px || px.x < 0 || (wrapWidth > 0 && px.x > wrapWidth)) return null
              return (
                <BacktestOrderTooltip
                  orders={wtOrderCard.orders}
                  point={px}
                  containerWidth={wrapWidth}
                  precision={wtOrderCard.precision}
                  tickets={wtOrderCard.tickets}
                />
              )
            })()}
          {/* The idea being edited owns the pane: a guide under the pointer saying
              what to click (or why not here). Its rim is drawn one level up, so it
              can enclose the drawing rail too. */}
          {activeTool === 'callout' && pinGuide && (
            <WorkbenchPinPrompt x={pinGuide.x} y={pinGuide.y} paneW={wrapW} target={pinGuide.target} />
          )}
          {/* On a narrow screen only the rule shows, and only while a pointer is on the chart:
              the words and the readout are at the top, where a finger does not cover them. */}
          {activeIdea && armedStep && ideaGuide && !composer && (!topReadout || pointerOnChart) && (
            <WorkbenchIdeaPrompt
              plate={!topReadout}
              lineLabel={topReadout ? markLineLabel(armedStep, armedStep, activeIdea.dir) : null}
              x={ideaGuide.x}
              y={ideaGuide.y}
              paneW={wrapW}
              text={PLACE_PROMPT[armedStep]}
              refusal={ideaGuide.refusal}
              readout={levelReadout({
                role: armedStep,
                price: ideaGuide.price,
                entry: activeIdea.entry?.value,
                stop: activeIdea.stop?.value,
                pipSize,
              })}
              role={armedStep}
              color={activeIdea.color}
            />
          )}
          {dragGuide && !topReadout && (
            <WorkbenchIdeaPrompt
              x={dragGuide.x}
              y={dragGuide.y}
              paneW={wrapW}
              text={MOVING_PROMPT[dragGuide.role]}
              refusal={dragGuide.refusal}
              readout={dragGuide.readout}
              role={dragGuide.role}
              color={dragGuide.color}
              rule={false}
            />
          )}
          {dragOnTop && dragGuide && (
            // No strip outside edit mode: the readout of a level being moved takes its place,
            // there only while the level is held. Inside the chart, so it never crosses the
            // drawing rail or the price axis.
            <div
              className="pointer-events-none absolute left-1 top-12 z-[106] overflow-hidden rounded-md shadow-popover"
              style={dragGuide.plotW > 8 ? { width: dragGuide.plotW - 8 } : { right: 4 }}
              data-idea-drag-bar
            >
              <WorkbenchIdeaTouchGuide
                text={MOVING_PROMPT[dragGuide.role]}
                refusal={dragGuide.refusal}
                readout={dragGuide.readout}
                role={dragGuide.role}
              />
            </div>
          )}
          {/* Trade-idea levels get NO hover card: hovering one lights the whole
              idea on the canvas, and clicking it opens its note. The card below
              belongs to the legacy buy/sell markers alone. */}
          {hoverNote && !composer && !selected && (
            <WorkbenchHoverNoteCard
              hover={hoverNote}
              wrapW={wrapW}
              wrapH={wrapH}
              onEdit={editLeg}
              onMouseEnter={cancelHoverClose}
              onMouseLeave={scheduleHoverClose}
              cardRef={hoverCardRef}
            />
          )}
          {ideaNote && (
            // Deliberately NOT behind a modal guard, unlike the legacy composer
            // below. Clicking away is a legitimate way to close this box: it
            // commits what was typed, and `swallowChartClickRef` stops the same
            // gesture from also planting the next level.
            <WorkbenchIdeaNote
              key={ideaNote.markId}
              role={ideaNote.role}
              // From the SAME derived idea the progress strip reads, so the
              // box's footer and the strip's ticks can never disagree about
              // what is still owed.
              nextStep={pendingStep(activeIdea, ideaNote.role)}
              // From that same derived idea: the box's bin and the panel's row
              // then name the level identically, because both read the label
              // stamped on the overlay.
              label={activeIdea?.marks.find((m) => m.id === ideaNote.markId)?.label}
              x={ideaNote.x}
              y={ideaNote.y}
              initialValue={ideaNote.initial}
              onSave={(text) => saveOverlayNote(ideaNote.markId, text)}
              onCommit={commitIdeaNote}
            />
          )}
          {composer && (
            // Modal guard: the note must be saved (or explicitly cancelled)
            // before anything else — swallow every pointer interaction so
            // clicks can't place markers, drag drawings, open menus or reach
            // the toolbars/chat while the composer is open. Sits just below
            // the composer card (z-[120]).
            <div
              className="fixed inset-0 z-[110] cursor-default"
              data-testid="composer-modal-guard"
              onPointerDown={(e) => e.stopPropagation()}
              onPointerMove={(e) => e.stopPropagation()}
              onPointerUp={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
              onContextMenu={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
            />
          )}
          {composer && (
            <WorkbenchEntryComposer
              x={composer.relX}
              y={composer.relY}
              paneW={wrapW}
              paneH={wrapH}
              price={composer.price}
              timeLabel={composer.timeLabel}
              defaultLeg={composer.defaultLeg}
              initialValue={composer.initialValue ?? ''}
              onSubmit={submitComposer}
              onCancel={cancelComposer}
            />
          )}
          {chart && notesPlaceable && (
            <WorkbenchNotes
              chart={chart}
              wrapRef={chartWrapRef}
              notes={notes}
              setNotes={setNotes}
              selectedId={selectedNoteId}
              setSelectedId={setSelectedNoteId}
              editingId={editingNoteId}
              setEditingId={setEditingNoteId}
              nonce={vpNonce}
              replay={series}
              resolvePin={(px) => pinTargetAt(px, null)}
            />
          )}
          {chart && selected && (
            <WorkbenchFloatingToolbar
              chart={chart}
              selected={selected}
              onExtend={handleExtend}
              onFibConfig={handleFibConfig}
              onDelete={handleDelete}
              onClone={cloneOverlay}
              onMutated={handleStyleMutated}
              onEditLeg={editLeg}
            />
          )}
          {/* The SECOND window of the pair above. The toolbar changes how the
              drawing looks; this records why it is there, and `chartContext.ts`
              renders it to the AI as `— reason: "…"` on that drawing's line.

              Below the anchor, because the toolbar is above it — the two never
              overlap, and the shape between them stays visible.

              Trade-idea levels and the legacy buy/sell markers are excluded by
              `isNotableDrawing`: they have their own note boxes, with their own
              words. Text and callout notes never reach here at all — they are
              React state, not overlays, so they are never `selected`. */}
          {chart && selected && isNotableDrawing(selected.name) && noteDismissedKey !== selected.key && (
            <WorkbenchDrawingNote
              key={selected.key}
              name={selected.name}
              x={selected.x}
              y={selected.y + 16}
              initialValue={selected.note ?? ''}
              // Only the drawing they have just finished takes the keyboard.
              autoFocus={selected.fresh === true}
              onSave={(text) => saveOverlayNote(selected.id, text)}
              onCommit={(text) => commitDrawingNote(selected.id, selected.key, text)}
            />
          )}
          {status.loading && (
            <div className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-md bg-popover px-3 py-1 text-caption text-muted-foreground shadow-popover">
              Loading candles…
            </div>
          )}
          {status.error && (
            <div className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-md bg-danger-dim px-3 py-1 text-caption text-danger">
              {status.error}
            </div>
          )}
          {!status.loading && !status.error && status.empty && symbol && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-body text-muted-foreground">
              No candle data for {symbol} {timeframe}
            </div>
          )}
        </div>

        {/* Trade ideas: the edit strip and the list live OUTSIDE the canvas
            wrapper — that wrapper carries the pointer handlers, so a click on
            either would otherwise fall through and place a level. */}
        {activeIdea && (
          <WorkbenchTradeIdeaStrip
            idea={activeIdea}
            currentStep={armedStep}
            // No `belowPill`: the mode pill that used to sit at top-centre is gone, so the
            // strip takes that row back rather than holding a gap open for nothing.
            clearLeft={ideaPanelOpen ? IDEA_PANEL_W : 0}
            onPickStep={(step) => {
              setArmedStep(step)
              armedStepRef.current = step
            }}
            onPickColor={(color) => setIdeaColor(activeIdea.ideaId, color)}
            onDelete={() => confirmDeleteIdea(activeIdea.ideaId)}
            onExit={exitIdeaEdit}
            touchGuide={
              dragInStrip && dragGuide
                ? {
                    // A placed level being moved: the same row says so, and has nothing to skip.
                    text: MOVING_PROMPT[dragGuide.role],
                    refusal: dragGuide.refusal,
                    readout: dragGuide.readout,
                    role: dragGuide.role,
                  }
                : topReadout && armedStep && !composer
                  ? {
                    text: (coarsePointer ? TAP_PROMPT : PLACE_PROMPT)[armedStep],
                    refusal: ideaGuide?.refusal ?? null,
                    readout:
                      pointerOnChart && ideaGuide
                        ? levelReadout({
                            role: armedStep,
                            price: ideaGuide.price,
                            entry: activeIdea.entry?.value,
                            stop: activeIdea.stop?.value,
                            pipSize,
                          })
                        : null,
                    role: armedStep,
                    onSkip: () => skipArmedStep(armedStep),
                  }
                : null
            }
          />
        )}
        {(
          <WorkbenchTradeIdeaPanel
            open={ideaPanelOpen}
            ideas={ideas}
            activeIdeaId={activeIdea?.ideaId ?? null}
            expanded={ideaExpanded}
            onToggleExpand={(id) =>
              setIdeaExpanded((prev) => {
                const next = new Set(prev)
                if (!next.delete(id)) next.add(id)
                return next
              })
            }
            onSelectIdea={openIdeaFromPanel}
            onSelectMark={openMarkFromPanel}
            onToggleHideMark={setMarkHidden}
            onDeleteMark={handleDelete}
            onDeleteIdea={confirmDeleteIdea}
            onClose={() => setIdeaPanelOpen(false)}
          />
        )}

        {/* The chart's one confirmation. Everything it guards is final — no
            undo, and the reasoning goes with the marks — so the word is typed
            rather than clicked. */}
        <ConfirmActionDialog
          open={!!confirmAsk?.open}
          onOpenChange={(next) => {
            if (!next) closeConfirmAsk()
          }}
          title={confirmAsk?.title}
          body={confirmAsk?.body}
          confirmLabel={confirmAsk?.confirmLabel}
          typeToConfirm={confirmAsk?.typeToConfirm}
          destructive={confirmAsk?.destructive ?? true}
          hideCancel={confirmAsk?.hideCancel ?? false}
          // A delete announcing "Done" describes what the chart already shows.
          noSuccess
          onConfirm={() => confirmAsk?.action()}
        />

        </div>

        {/* The walkthrough's band and trades pane. Keyed by the ATTACH, not the btid: a re-run the
            server answered with the same row still arrives as a fresh replay (its window and its
            autoplay are read once). Mounted on every symbol and while hidden, rendering nothing
            then: the replay's cursor lives in it, and a trip to another market (or a hide) must
            not lose the place the user had played to. Preparing (or failing) a new run hides it
            the same way rather than unmounting it: a re-run that fails gives the user back the
            run they had, where they left it — a remount would re-read its attach-time cursor and
            autoplay. The status band alone speaks meanwhile. */}
        {wtRun && (
          <WorkbenchWalkthrough
            key={wtRun.attachId}
            run={wtRun}
            chart={chart}
            series={series}
            symbol={symbol}
            timeframe={timeframe}
            shown={showWalkthrough && walkthroughOverlay.phase.kind === 'idle'}
            drawIdsRef={drawIdsRef}
            drawingsVersion={drawVersion}
            isDrawingExempt={isDrawingExempt}
            onDrawingsHidden={handleDrawingsHidden}
            holdViewport={holdReplayViewport}
            onCutoffChange={handleCutoffChange}
            onMarkerClick={handleWtMarkerClick}
            tokenHost={chartWrapRef}
            currentCode={currentCode}
            hasCode={hasCode}
          />
        )}
        {walkthroughOverlay.phase.kind !== 'idle' && (
          <WorkbenchReplayStatus
            status={walkthroughOverlay.phase.kind === 'preparing' ? 'preparing' : 'error'}
            reason={walkthroughOverlay.phase.kind === 'preparing' ? walkthroughOverlay.phase.reason : undefined}
            runStatus={walkthroughOverlay.phase.kind === 'preparing' ? walkthroughOverlay.phase.runStatus : null}
            progressPct={walkthroughOverlay.phase.kind === 'preparing' ? walkthroughOverlay.phase.progressPct : 0}
            slow={walkthroughOverlay.slow}
            errorMessage={walkthroughOverlay.phase.kind === 'error' ? walkthroughOverlay.phase.message : undefined}
            errorAction={walkthroughOverlay.phase.kind === 'error' ? walkthroughOverlay.phase.action : undefined}
            onRetry={walkthroughOverlay.runWalkthrough}
            onManageBilling={onManageBilling}
            onDismiss={walkthroughOverlay.dismissError}
          />
        )}
        </div>

        {/* The idea being edited owns the whole working surface, so the rim encloses
            the drawing rail as well as the chart — its edges land on this row's own
            edges instead of the seam between the two, and the BUY/SELL tools the
            user is working with sit inside it. */}
        {activeIdea && (
          <div
            // Test hook: pointer-events-none means elementFromPoint can never
            // return this, so the mobile e2e compares computed z-index instead.
            data-idea-rim
            className="pointer-events-none absolute inset-0 z-[100] rounded-md"
            style={{ border: `2px solid ${activeIdea.color}` }}
          />
        )}
      </div>

      <WorkbenchContextMenu
        menu={
          ctxMenu
            ? {
                x: ctxMenu.x,
                y: ctxMenu.y,
                items: [
                  { label: 'Reset chart view', icon: RotateCcw, onClick: resetChartView },
                  { separator: true },
                  {
                    label: `Remove ${ctxMenu.drawCount} drawing${ctxMenu.drawCount === 1 ? '' : 's'}`,
                    icon: Trash2,
                    disabled: ctxMenu.drawCount === 0,
                    onClick: removeAllDrawings,
                  },
                  {
                    label: `Remove ${indicators.length} indicator${indicators.length === 1 ? '' : 's'}`,
                    icon: Trash2,
                    disabled: indicators.length === 0,
                    onClick: removeAllIndicators,
                  },
                  { separator: true },
                  { label: 'Settings…', icon: Settings, onClick: () => setSettingsOpen(true) },
                  { separator: true },
                  // Not "drawings": indicators, chart type and timeframe travel
                  // too, and a label promising less would surprise on the way in.
                  { label: 'Export chart setup…', icon: Download, disabled: !chart, onClick: handleExportSetup },
                  { label: 'Import chart setup…', icon: Upload, onClick: () => importInputRef.current?.click() },
                ],
              }
            : null
        }
        onClose={() => setCtxMenu(null)}
      />
      {/* Off-screen rather than display:none — a hidden input still opens the
          picker, and resetting value lets the same file be imported twice. */}
      <input
        ref={importInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        aria-label="Import chart setup"
        onChange={(e) => {
          const file = e.target.files?.[0]
          e.target.value = ''
          if (file) void handleImportFile(file)
        }}
      />
      <WorkbenchContextMenu
        menu={
          deleteMenu
            ? ({
                x: deleteMenu.x,
                y: deleteMenu.y,
                items: [
                  {
                    label: `Remove ${deleteMenu.drawCount} drawing${deleteMenu.drawCount === 1 ? '' : 's'}`,
                    icon: Trash2,
                    disabled: deleteMenu.drawCount === 0,
                    onClick: removeAllDrawings,
                  },
                  {
                    label: `Remove ${indicators.length} indicator${indicators.length === 1 ? '' : 's'}`,
                    icon: Trash2,
                    disabled: indicators.length === 0,
                    onClick: removeAllIndicators,
                  },
                  { separator: true },
                  {
                    label: 'Remove all',
                    icon: Trash2,
                    disabled: deleteMenu.drawCount === 0 && indicators.length === 0,
                    // Asks first: this one takes the trade ideas too.
                    onClick: confirmRemoveAll,
                  },
                ],
              } satisfies MenuSpec)
            : null
        }
        onClose={() => setDeleteMenu(null)}
      />
      <WorkbenchSymbolSearch
        open={symbolSearchOpen}
        onOpenChange={setSymbolSearchOpen}
        symbols={symbols}
        value={symbol}
        onChange={handleSymbolChange}
        seed={symbolSeed}
      />
      <WorkbenchIndicatorSearch
        open={indicatorsOpen}
        onOpenChange={setIndicatorsOpen}
        addedNames={indicators.map((i) => i.name)}
        volumePresent={volume.present}
        onAdd={addIndicator}
      />
      {chart && settingsInstance && settingsOption && (
        <WorkbenchIndicatorSettings
          chart={chart}
          instance={settingsInstance}
          option={settingsOption}
          onChange={(patch: Partial<IndicatorSettings>) => updateInstanceSettings(settingsInstance.id, patch)}
          onClose={() => setSettingsId(null)}
        />
      )}
      <WorkbenchSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={chartSettings}
        onChange={(patch) => setChartSettings((s) => ({ ...s, ...patch }))}
        onReset={() => setChartSettings({})}
        onResetOnboarding={() => {
          // Clear the done flag (so a fresh visit shows the tour too) and
          // restart it right away, out from under the settings dialog.
          onboardingStorage.reset()
          setSettingsOpen(false)
          setOnbStep(0)
        }}
      />
      {volumeSettingsOpen && (
        <WorkbenchVolumeSettings
          up={volume.up ?? volColors(isDark).up}
          down={volume.down ?? volColors(isDark).down}
          onChange={(patch: Partial<VolumeState>) => setVolume((v) => ({ ...v, ...patch }))}
          onReset={() => setVolume((v) => ({ ...v, up: null, down: null }))}
          onClose={() => setVolumeSettingsOpen(false)}
        />
      )}
      <ShowStateDialog open={showState} onOpenChange={setShowState} state={stateSnapshot} />
      {/* First-run informative tour — paged with Next/Skip, blocks the app underneath. */}
      {onbActive && typeof onbStep === 'number' && (
        <OnboardingTour
          targetSelectors={ONB_STEPS[onbStep].targets}
          placement={ONB_STEPS[onbStep].placement}
          copy={ONB_STEPS[onbStep].copy}
          step={onbStep + 1}
          totalSteps={ONB_STEPS.length}
          onNext={handleOnbNext}
          onSkip={handleOnbSkip}
        />
      )}
    </div>
  )
}
